import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import Map from 'ol/Map';
import View from 'ol/View';
import type { Layer } from 'ol/layer';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import MVT from 'ol/format/MVT';
import { Draw, Modify, Snap } from 'ol/interaction';
import { Fill, Stroke, Style, Circle as CircleStyle, Text as OlText, Icon } from 'ol/style';
import { fromLonLat, toLonLat } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
import WFS from 'ol/format/WFS';
import { Feature } from 'ol';
import type { FeatureLike } from 'ol/Feature';
import { LineString, Point, Polygon } from 'ol/geom';
import { defaults as defaultControls, ScaleLine } from 'ol/control';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import { buffer as extentBuffer } from 'ol/extent';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  LrsCalibrationPoint,
  LrsCarriageway,
  LrsCorridorInventory,
  LrsDocumentRef,
  lrsMetaFromDocument,
  measureKmFromLrsMeta,
  lrsChainageLabel,
  LrsEvent,
  LrsLocateResult,
  LrsMaintenanceEvent,
  LrsMeasurementRun,
  LrsPavementCondition,
  LrsQcReport,
  LrsNetworkQcReport,
  LrsCalibrationAccuracyReport,
  LrsBulkImportResult,
  LrsReportCatalogEntry,
  LrsExportReport,
  LrsSurveyDeviationReport,
  LrsAcceptanceEvidenceReport,
  LrsRoute,
  LrsNetwork,
  LrsSubnetwork,
  LrsSectionLink,
  LrsService,
  LrsUtilityAsset,
  LrsClashReport,
  LrsRouteExtensionLog,
  LrsRouteExtensionResult,
  LrsExcavationPermit,
  LrsDemoSeedResult,
  LrsLane,
  LrsRouteSnapshot,
  LrsVerticalPvi,
  LrsGroundSample,
  LrsSection,
  LrsRouteSegment,
  LrsEventType,
  LrsLandParcel,
  LrsParcelStatus,
} from 'src/app/shared/services/lrs.service';
import {
  defaultAssetLabel,
  getLrsAssetTypeMeta,
  isLrsAssetEventType,
  isLinearAssetType,
  LRS_ASSET_EVENT_TYPES,
  LRS_EVENT_BEHAVIORS,
  LRS_GRADES,
  LRS_LINEAR_EVENT_TYPES,
  LRS_ROUTE_LENGTH_MODES,
  LRS_UTILITY_TYPES,
  defaultGradeForEventType,
  defaultGradeForUtilityType,
  inferLrsGrade,
  specFieldsForAsset,
  LrsAssetTypeMeta,
  LrsGrade,
  LrsWorkbenchPanel,
} from 'src/app/shared/constants/lrs-asset-types';
import { formatChainageFromMeters, parseChainageMeters } from 'src/app/shared/utils/chainage.util';
import {
  applySymbolProperties,
  iconPathForAssetType,
  LRS_DEFAULT_CLUSTER_DISTANCE,
  clusterDistanceForZoom,
  preloadLrsIcons,
  clearLrsIconStyleCache,
  styleCluster,
  styleLinearAsset,
  styleUtilityFeature,
  symbolCodeForAssetType,
} from 'src/app/shared/utils/lrs-asset-symbology';
import { environment } from 'src/environments/environment';
import { GeospatialMapBridgeService } from 'src/app/shared/services/geospatial-map-bridge.service';
import { LrsDashboardService, LrsQuickAction } from 'src/app/shared/services/lrs-dashboard.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { RbacService } from 'src/app/shared/services/rbac.service';
import {
  UnmAsset,
  UnmDomain,
  UnmPermitAssessResult,
  UnmService,
  UnmTraceDirection,
  UnmTraceEdge,
  UnmTraceRow,
  UNM_ASSET_PROPERTY_FIELDS,
} from 'src/app/shared/services/unm.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { LrsAssetDetailsComponent } from '../lrs-asset-details/lrs-asset-details.component';
import { LrsLayerTreeComponent } from '../lrs-layer-tree/lrs-layer-tree.component';
import {
  LrsCadastralPanelComponent,
  PARCEL_STATUS_META,
} from '../lrs-cadastral-panel/lrs-cadastral-panel.component';
import {
  countFeaturesForLeaf,
  defaultLayerVisibility,
  flattenCatalogLeaves,
  isCatalogFeatureVisible,
  toggleCatalogNode,
  type CatalogFeatureProps,
} from 'src/app/shared/constants/lrs-layer-catalog';

type BaseMapKey = 'osm' | 'googleSatellite' | 'googleHybrid';

type LrsAssetSearchHit = {
  kind: 'highway' | 'utility';
  id: number;
  label: string;
  type: string;
  chainage: string;
};

@Component({
  selector: 'app-lrs-manager',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    LrsAssetDetailsComponent,
    LrsLayerTreeComponent,
    LrsCadastralPanelComponent,
  ],
  templateUrl: './lrs-manager.component.html',
  styleUrls: ['./lrs-manager.component.css'],
})
export class LrsManagerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  /** Panel-only mode: LRS overlays attach to the shared GIS map (unified geospatial workspace). */
  @Input() integratedMode = false;
  /** Renders only the tool panel (for GIS editor LRS sidebar tab). */
  @Input() sidebarPanelOnly = false;
  /** When false (GIS tab inactive), map clicks are ignored in integrated mode. */
  @Input() mapInteractActive = true;

  @ViewChild('mapHost', { static: true }) mapHost!: ElementRef<HTMLDivElement>;

  routes: LrsRoute[] = [];
  selectedRouteId: number | null = null;
  selectedRoute: LrsRoute | null = null;
  assetEvents: LrsEvent[] = [];
  sectionLinks: LrsSectionLink[] = [];
  linkedDocuments: LrsDocumentRef[] = [];
  calibrationPoints: LrsCalibrationPoint[] = [];
  qcReport: LrsQcReport | null = null;
  networkQcReport: LrsNetworkQcReport | null = null;
  calibrationReport: LrsCalibrationAccuracyReport | null = null;

  loading = false;
  seedingDemo = false;
  embedded = false;
  demoSeedSummary: LrsDemoSeedResult | null = null;
  private demoAutoStarted = false;
  private demoWalkthroughPending = false;
  private lastDemoRun: string | null = null;
  private querySub?: Subscription;
  mapError = '';
  statusMessage = '';

  projectId = '';
  documentId: number | null = null;
  private lastHydratedDocumentId: number | null = null;
  private pendingDocumentPan = false;

  measureKm = 0;
  measureToKm = 0;
  locateResult: LrsLocateResult | null = null;
  boqAtChainage: {
    km?: number;
    from_km?: number;
    to_km?: number;
    item_count: number;
    boqs: Array<{
      boq_id: number;
      boq_code: string;
      boq_name: string;
      item_count: number;
      from_km?: number;
      to_km?: number;
    }>;
    items: Array<{
      boq_id: number;
      boq_code: string;
      item_code: string;
      item_description: string;
      unit: string;
      quantity: number;
      final_line_total: number;
      from_km?: number;
      to_km?: number;
    }>;
  } | null = null;
  boqAtChainageLoading = false;
  generatingBoq = false;
  referenceInput = '';
  /** Which chainage field the next map click will populate. */
  chainagePickTarget: 'from' | 'to' = 'from';
  chainagePickActive = false;
  chainageValidationError = '';
  assetSearchQuery = '';
  placeAssetMode = false;
  private searchPanTimer: ReturnType<typeof setTimeout> | null = null;

  newRouteCode = '';
  newRouteName = '';
  newRouteDesignSpeed: number | null = 100;
  newRouteTerrain = 'plain';
  newRouteRowWidth: number | null = 60;
  newRouteCarriageway = '4L-divided';
  drawMode = false;
  realignDrawMode = false;
  routeEditVerticesMode = false;
  osmDrawSnap?: Snap;
  private lastDrawSnapTol: number | null = null;

  networks: LrsNetwork[] = [];
  subnetworks: LrsSubnetwork[] = [];
  selectedNetworkId: number | null = null;
  selectedSubnetworkId: number | null = null;
  newNetworkCode = '';
  newNetworkName = '';
  newNetworkRegion = '';
  newNetworkCircle = '';
  newNetworkDivision = '';
  newNetworkSubDivision = '';
  editNetworkRegion = '';
  editNetworkCircle = '';
  editNetworkDivision = '';
  editNetworkSubDivision = '';
  newSubnetworkCode = '';
  newSubnetworkName = '';
  applyingRepairs = false;
  newRouteClass: '' | 'NH' | 'SH' | 'MDR' | 'other' = '';
  routeEditClass: '' | 'NH' | 'SH' | 'MDR' | 'other' = '';
  routeClassFilter = '';
  carriagewaySide: 'L' | 'R' | 'C' | 'BOTH' = 'BOTH';
  sections: LrsSection[] = [];
  routeSegments: LrsRouteSegment[] = [];
  selectedSectionId: number | null = null;
  selectedSegmentId: number | null = null;
  assignSectionId: number | null = null;
  assignParentSegmentId: number | null = null;
  assignCarriagewaySide: '' | 'L' | 'R' | 'C' | 'BOTH' = '';
  newSectionCode = '';
  newSectionName = '';
  newSectionFromKm = 0;
  newSectionToKm = 1;
  newSectionSurface = '';
  newSectionLaneWidth: number | null = null;
  catalogEventTypes: LrsEventType[] = [];

  get hasNetwork(): boolean {
    return this.selectedNetworkId != null;
  }

  get filteredRoutes(): LrsRoute[] {
    if (!this.routeClassFilter) return this.routes;
    return this.routes.filter((r) => (r.route_class || '') === this.routeClassFilter);
  }

  get assetsLocked(): boolean {
    const r = this.selectedRoute;
    return !(r?.map_ok && r?.road_ok && r?.lrs_ok);
  }

  osmRouteRef = '';
  roadProjectImportId = '';
  chainageIntervalKm = 1;
  chainagePersist = false;

  assetTypes: LrsAssetTypeMeta[] = [...LRS_ASSET_EVENT_TYPES];
  linearEventTypes = LRS_LINEAR_EVENT_TYPES;
  utilityTypes = LRS_UTILITY_TYPES;
  workbenchTabs: { id: LrsWorkbenchPanel; label: string }[] = [
    { id: 'assets', label: 'Assets' },
    { id: 'alignment', label: 'Alignment' },
    { id: 'utilities', label: 'Utilities' },
    { id: 'corridor', label: 'Corridor' },
    { id: 'cadastral', label: 'Cadastral' },
    { id: 'attributes', label: 'Attributes' },
    { id: 'survey', label: 'Survey/PCI' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'network', label: 'Network' },
    { id: 'unm', label: 'UNM' },
    { id: 'route', label: 'Route' },
    { id: 'clash', label: 'Clash' },
    { id: 'reports', label: 'Reports' },
    { id: 'field', label: 'Field' },
  ];
  readonly primaryWorkbenchTabIds: LrsWorkbenchPanel[] = ['assets', 'alignment', 'utilities', 'corridor', 'cadastral'];
  moreMenuOpen = false;
  activePanel: LrsWorkbenchPanel = 'assets';

  get primaryWorkbenchTabs(): { id: LrsWorkbenchPanel; label: string }[] {
    return this.workbenchTabs.filter((t) => this.primaryWorkbenchTabIds.includes(t.id));
  }

  get moreWorkbenchTabs(): { id: LrsWorkbenchPanel; label: string }[] {
    return this.workbenchTabs.filter((t) => !this.primaryWorkbenchTabIds.includes(t.id));
  }

  get morePanelActive(): boolean {
    return this.moreWorkbenchTabs.some((t) => t.id === this.activePanel);
  }

  readonly workbenchGroups: {
    label: string;
    tools: { id: LrsWorkbenchPanel; label: string; hint: string }[];
  }[] = [
    {
      label: 'Route',
      tools: [
        { id: 'chainage', label: 'Chainage', hint: 'Locate a km, BOQ at chainage, km markers' },
        { id: 'calibration', label: 'Calibration', hint: 'Km stones and GPS to chainage' },
        { id: 'maplayers', label: 'Map layers', hint: 'Layer visibility and edge-offset locate' },
      ],
    },
    {
      label: 'Inventory',
      tools: [
        { id: 'assets', label: 'Assets', hint: 'Signs, culverts, bridges, barriers, lighting' },
        { id: 'utilities', label: 'Utilities', hint: 'Corridor utilities by offset and depth' },
        { id: 'attributes', label: 'Attributes', hint: 'Pavement, geometry and cross-section by km range' },
      ],
    },
    {
      label: 'Design',
      tools: [
        { id: 'alignment', label: 'Alignment', hint: 'Vertical PVIs and existing ground' },
        { id: 'corridor', label: 'Corridor', hint: 'Corridor inventory for a km range' },
        { id: 'cadastral', label: 'Cadastral', hint: 'Land parcels and right-of-way' },
      ],
    },
    {
      label: 'Operations',
      tools: [
        { id: 'survey', label: 'Survey/PCI', hint: 'NSV runs and pavement condition' },
        { id: 'maintenance', label: 'Maintenance', hint: 'Maintenance history by km' },
        { id: 'field', label: 'Field', hint: 'GPS proximity search and lateral offsets' },
        { id: 'clash', label: 'Clash', hint: 'Excavation clash checks before dig permits' },
        { id: 'reports', label: 'Reports', hint: 'SLD, inventories and QC as PDF / Excel / CSV' },
      ],
    },
    {
      label: 'Network',
      tools: [
        { id: 'network', label: 'Sections', hint: 'Section, segment and subsegment by carriageway' },
        { id: 'route', label: 'Edit route', hint: 'Adjust the route geometry and extents' },
        { id: 'unm', label: 'Utility network', hint: 'Connect and trace utility network assets' },
      ],
    },
  ];
  pickerOpen = false;

  get activeToolLabel(): string {
    for (const g of this.workbenchGroups) {
      const t = g.tools.find((x) => x.id === this.activePanel);
      if (t) return t.label;
    }
    return 'Tools';
  }

  pickTool(panel: LrsWorkbenchPanel): void {
    this.pickerOpen = false;
    this.setActivePanel(panel);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentClickClosePicker(event: MouseEvent): void {
    if (!this.pickerOpen) return;
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.tool-picker')) this.pickerOpen = false;
  }

  /** Asset and utility creation read their measure from the chainage inputs. */
  get showChainageSection(): boolean {
    return !this.sidebarPanelOnly || ['chainage', 'assets', 'utilities'].includes(this.activePanel);
  }

  get chainageAsPanel(): boolean {
    return this.sidebarPanelOnly && this.activePanel === 'chainage';
  }

  showToolSection(panel: LrsWorkbenchPanel): boolean {
    return !this.sidebarPanelOnly || this.activePanel === panel;
  }

  // ---- Compact sidebar layout (sidebarPanelOnly) ----
  private readonly sidebarLayoutKey = 'lrs-sidebar-layout';
  sidebarModePref: 'setup' | 'workbench' = 'workbench';
  importMode: 'osm' | 'shapefile' | 'road' | 'draw' = 'osm';
  cardOpen: Record<string, boolean> = {};

  /** Workbench needs a route, so fall back to setup until one is selected. */
  get sidebarMode(): 'setup' | 'workbench' {
    return this.selectedRoute ? this.sidebarModePref : 'setup';
  }

  get showSetup(): boolean {
    return !this.sidebarPanelOnly || this.sidebarMode === 'setup';
  }

  get showWorkbench(): boolean {
    return !this.sidebarPanelOnly || this.sidebarMode === 'workbench';
  }

  setSidebarMode(mode: 'setup' | 'workbench'): void {
    this.sidebarModePref = mode;
    this.persistSidebarLayout();
  }

  isCardOpen(key: string): boolean {
    if (!this.sidebarPanelOnly) return true;
    if (key in this.cardOpen) return this.cardOpen[key];
    switch (key) {
      case 'network':
        return !this.hasNetwork;
      case 'validation':
        return this.assetsLocked;
      case 'routes':
        return !this.selectedRoute;
      case 'qc':
        return true;
      default:
        return false;
    }
  }

  onCardToggle(key: string, event: Event): void {
    if (!this.sidebarPanelOnly) return;
    const el = event.target as HTMLDetailsElement;
    if (el.classList.contains('as-panel')) return;
    const open = el.open;
    if (this.isCardOpen(key) === open) return;
    this.cardOpen = { ...this.cardOpen, [key]: open };
    this.persistSidebarLayout();
  }

  get networkSummary(): string {
    const n = this.networks.find((x) => x.id === this.selectedNetworkId);
    if (!n) return 'Not set';
    const s = this.subnetworks.find((x) => x.id === this.selectedSubnetworkId);
    return s ? `${n.network_code} / ${s.subnetwork_code}` : n.network_code;
  }

  get validationPassed(): number {
    const r = this.selectedRoute;
    return r ? [r.map_ok, r.road_ok, r.lrs_ok].filter(Boolean).length : 0;
  }

  get validationSummary(): string {
    return this.selectedRoute ? `${this.validationPassed}/3 passed` : '';
  }

  get validationTooltip(): string {
    const r = this.selectedRoute;
    if (!r) return '';
    const mark = (ok: boolean | null | undefined) => (ok ? '✓' : '✗');
    return (
      `Map matches corridor ${mark(r.map_ok)} · Road geometry clean ${mark(r.road_ok)} · ` +
      `LRS calibrated ${mark(r.lrs_ok)} — click to review`
    );
  }

  goToValidation(): void {
    this.cardOpen = { ...this.cardOpen, validation: true };
    this.setSidebarMode('setup');
  }

  get routesSummary(): string {
    return `${this.routes.length} route${this.routes.length === 1 ? '' : 's'}`;
  }

  get chainageSummary(): string {
    return `km ${Number(this.measureKm || 0).toFixed(3)}`;
  }

  get calibrationSummary(): string {
    const n = this.calibrationPoints.length;
    return `${n} point${n === 1 ? '' : 's'}`;
  }

  get layersSummary(): string {
    const on = Object.values(this.nhaiLayerVisibility || {}).filter(Boolean).length;
    return `${on} on`;
  }

  get qcSummary(): string {
    const reports = [this.qcReport, this.networkQcReport, this.calibrationReport].filter(Boolean);
    if (!reports.length) return '';
    return reports.every((r) => r!.ok) ? 'OK' : 'Review';
  }

  private restoreSidebarLayout(): void {
    try {
      const raw = sessionStorage.getItem(this.sidebarLayoutKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        mode?: 'setup' | 'workbench';
        importMode?: LrsManagerComponent['importMode'];
        cards?: Record<string, boolean>;
        panel?: LrsWorkbenchPanel;
      };
      if (saved.mode === 'setup' || saved.mode === 'workbench') this.sidebarModePref = saved.mode;
      if (saved.panel && this.workbenchGroups.some((g) => g.tools.some((t) => t.id === saved.panel))) {
        this.activePanel = saved.panel;
      }
      if (saved.importMode) this.importMode = saved.importMode;
      if (saved.cards && typeof saved.cards === 'object') this.cardOpen = saved.cards;
    } catch {
      /* ignore corrupt storage */
    }
  }

  persistSidebarLayout(): void {
    if (!this.sidebarPanelOnly) return;
    try {
      sessionStorage.setItem(
        this.sidebarLayoutKey,
        JSON.stringify({
          mode: this.sidebarModePref,
          importMode: this.importMode,
          cards: this.cardOpen,
          panel: this.activePanel,
        })
      );
    } catch {
      /* storage unavailable */
    }
  }

  onSidebarRouteSelect(routeId: number | null): void {
    if (routeId != null) this.selectRoute(routeId);
  }
  eventBehaviors = LRS_EVENT_BEHAVIORS;

  newEventType = 'sign';
  newEventLabel = '';
  assetFilterType = '';
  assetFilterFromKm: number | null = null;
  assetFilterToKm: number | null = null;
  assetFilterOffsetFromM: number | null = null;
  assetFilterOffsetToM: number | null = null;
  assetFilterSide = '';
  bulkImportCsv = '';
  reportCatalog: LrsReportCatalogEntry[] = [];
  reportType = 'asset-inventory';
  reportFormat: 'json' | 'csv' | 'xlsx' | 'pdf' = 'xlsx';
  reportPreview: LrsExportReport | null = null;
  surveyObservationsCsv = '';
  surveyReport: LrsSurveyDeviationReport | null = null;
  acceptanceReport: LrsAcceptanceEvidenceReport | null = null;
  assetSide = '';
  highwayGrade: LrsGrade = 'overground';
  conditionGrade = '';
  assetRef = '';
  lateralOffsetM = 0;
  edgeOffsetM: number | null = null;
  nhaiLayerVisibility: Record<string, boolean> = defaultLayerVisibility(false);
  nhaiLayerCounts: Record<string, number> = {};
  nhaiExpandedGroups: Record<string, boolean> = {};
  locateChainage = '';
  locateSide = 'L';
  locateEdgeOffsetM: number | null = 3;
  locateResults: { kind: string; id: number; type?: string; label?: string; measure_m?: number; edge_offset_m?: number }[] = [];
  locateStatus = '';
  createSpecValues: Record<string, string | number | null> = {};
  eventBehavior = 'default';
  assetValidFrom = '';
  assetValidTo = '';
  assetCounts: Record<string, number> = {};

  routeLengthModes = LRS_ROUTE_LENGTH_MODES;
  routeLengthMode: 'increase' | 'decrease' = 'increase';
  extendPosition: 'start' | 'end' = 'end';
  extendMeters = 100;
  extendBehavior = 'cover';
  routeTargetLengthKm: number | null = null;
  routeEditCode = '';
  routeEditName = '';
  routeEditOriginKm = 0;
  // IRC:73 design attributes
  routeEditDesignSpeed: number | null = null;
  routeEditTerrain = '';
  routeEditRowWidth: number | null = null;
  routeEditCarriageway = '';
  readonly terrainTypes = [
    { id: 'plain', label: 'Plain (cross slope ≤ 10%)' },
    { id: 'rolling', label: 'Rolling (10–25%)' },
    { id: 'mountainous', label: 'Mountainous (25–60%)' },
    { id: 'steep', label: 'Steep (> 60%)' },
  ];
  readonly carriagewayConfigs = ['SL', 'IL', '2L', '2L+PS', '4L-divided', '6L-divided', '8L-divided'];
  readonly designSpeeds = [30, 40, 50, 65, 80, 100, 120];
  routeExtensions: LrsRouteExtensionLog[] = [];

  clashReport: LrsClashReport | null = null;
  clashMaxDepthM = 1.5;
  clashMinVerticalSepM = 0.5;
  clashMin3dSepM = 1;
  shapefileLayer: 'all' | 'route' | 'events' | 'utilities' | 'clash' = 'all';
  shapefileImporting = false;
  routeSnapshots: LrsRouteSnapshot[] = [];
  excavationPermits: LrsExcavationPermit[] = [];
  unmPermitResult: UnmPermitAssessResult | null = null;
  unmPermitWidthM = 5;
  unmAssessing = false;

  unmDomains: UnmDomain[] = [];
  unmAssets: UnmAsset[] = [];
  unmTraceResults: UnmTraceRow[] = [];
  unmAssetType = 'FIBER';
  unmAssetCode = '';
  unmAssetName = '';
  unmDomainId: number | null = null;
  unmChainageKm = 0;
  unmOffsetM = 0;
  unmOffsetSide: 'L' | 'R' | 'C' = 'R';
  unmDepthM: number | null = -1.2;
  unmFromNodeId: number | null = null;
  unmToNodeId: number | null = null;
  unmTraceNodeId: number | null = null;
  unmTraceDirection: UnmTraceDirection = 'downstream';
  unmContainerAssetId: number | null = null;
  unmContainedAssetId: number | null = null;
  unmStructureAssetId: number | null = null;
  unmAttachedAssetId: number | null = null;
  unmTierId: number | null = null;
  unmConnectPickMode = true;
  unmPickStep: 'from' | 'to' = 'from';
  unmProperties: Record<string, string | number> = {};
  unmEdges: UnmTraceEdge[] = [];
  unmTraceEdges: UnmTraceEdge[] = [];

  fieldRadiusM = 500;
  nearbyItems: Array<Record<string, unknown>> = [];

  attributeEventType = 'pavement';
  attributeLabel = '';
  attributeDataJson = '{}';

  measurementRuns: LrsMeasurementRun[] = [];
  selectedRunId: number | null = null;
  pavementConditions: LrsPavementCondition[] = [];
  pciValue: number | null = null;
  iriValue: number | null = null;
  surfaceType = 'BT';

  maintenanceEvents: LrsMaintenanceEvent[] = [];
  maintWorkType = 'repair';
  maintContractor = '';
  maintCostInr: number | null = null;
  maintWarrantyMonths: number | null = 12;

  utilityAssets: LrsUtilityAsset[] = [];
  utilityType = 'water';
  utilityLabel = '';
  utilityDepthM: number | null = null;
  utilityDiameterMm: number | null = null;
  utilityIsLinear = false;
  utilityGrade: LrsGrade = 'overground';
  readonly grades = LRS_GRADES;
  readonly crossingTypes = LRS_ASSET_EVENT_TYPES.filter((t) =>
    t.id === 'crossover' || t.id === 'underpass' || t.id === 'overpass'
  );

  carriageways: LrsCarriageway[] = [];
  carriagewayCode = 'MAIN';
  carriagewayLanes = 4;
  carriagewayDirection = 'BOTH';
  carriagewayOffsetM = 0;

  gpsLng: number | null = null;
  gpsLat: number | null = null;
  gpsCapturing = false;
  historyAtTime = '';
  pendingOfflineCount = 0;
  offlineSyncing = false;
  private readonly offlineQueueKey = 'lrs_offline_asset_queue';
  private onlineListener = () => this.syncOfflineQueue();
  corridorSummary: Record<string, number> | null = null;
  corridorLayerQa: LrsCorridorInventory['layer_qa'] | null = null;
  corridorOffsetFromM: number | null = null;
  corridorOffsetToM: number | null = null;
  corridorFilterSide = '';
  private lastCorridor: LrsCorridorInventory | null = null;

  segFromKm = 0;
  segToKm = 1;
  /** Route id from query string — applied once routes load. */
  private pendingRouteId: number | null = null;

  baseMapKey: BaseMapKey = 'osm';
  showGeoserverWms = false;
  geoserverLayer = 'lrs:routes_wms';
  clusterEnabled = true;
  clusterDistancePx = LRS_DEFAULT_CLUSTER_DISTANCE;
  serverClusterMode = false;
  serverClusterMethod: 'dbscan' | 'grid' | 'heatmap' | 'utility_network' = 'dbscan';
  mvtMode = false;
  serverClusterMeta: {
    method?: string;
    cluster_count?: number;
    singleton_count?: number;
    eps_m?: number;
    cell_m?: number;
    total_groups?: number;
  } | null = null;

  lanesMode = false;
  lanes: LrsLane[] = [];

  verticalPvis: LrsVerticalPvi[] = [];
  groundSamples: LrsGroundSample[] = [];
  pviEditId: number | null = null;
  pviMeasureKm = 0;
  pviElevM = 420;
  pviGradeIn = 0;
  pviGradeOut = 0;
  pviCurveLengthM = 120;
  pviLabel = '';
  groundMeasureKm = 0;
  groundElevM = 420;
  groundSource: 'survey' | 'dem' | 'import' = 'survey';
  alignmentStatus = '';

  formatChainageFromMeters = formatChainageFromMeters;

  private map?: Map;
  private baseLayers: Record<BaseMapKey, TileLayer> = {} as Record<BaseMapKey, TileLayer>;
  private baseLayer?: TileLayer;
  private geoserverLayerRef?: ImageLayer<ImageWMS>;

  private routeSource = new VectorSource();
  private routeLayer?: VectorLayer<VectorSource>;
  private pointEventRawSource = new VectorSource();
  private pointEventClusterSource = new Cluster({
    distance: LRS_DEFAULT_CLUSTER_DISTANCE,
    minDistance: 16,
    source: this.pointEventRawSource,
  });
  private linearEventSource = new VectorSource();
  private chainageSource = new VectorSource();
  private calibSource = new VectorSource();
  private segPreviewSource = new VectorSource();
  private sectionSource = new VectorSource();
  private markerSource = new VectorSource();
  private pavementSource = new VectorSource();
  private maintenanceSource = new VectorSource();
  private utilityLineSource = new VectorSource();
  private utilityPointRawSource = new VectorSource();
  private utilityPointClusterSource = new Cluster({
    distance: 35,
    minDistance: 14,
    source: this.utilityPointRawSource,
  });
  private carriagewaySource = new VectorSource();
  private lanesSource = new VectorSource();
  private lanesLayer?: VectorLayer<VectorSource>;
  private carriagewayLayer?: VectorLayer<VectorSource>;
  private chainageLayer?: VectorLayer<VectorSource>;
  private clashSource = new VectorSource();
  private offsetLineSource = new VectorSource();
  private offsetConnectorSource = new VectorSource();
  private serverClusterSource = new VectorSource();
  private serverClusterLayer?: VectorLayer<VectorSource>;
  private mvtLayer?: VectorTileLayer;
  private moveEndKey?: EventsKey;
  private clusterReloadTimer?: ReturnType<typeof setTimeout>;
  private clusterDistanceRaf = 0;

  private utilityLineLayer?: VectorLayer<VectorSource>;
  private highlightSource = new VectorSource();
  private highlightLayer?: VectorLayer<VectorSource>;
  private unmSource = new VectorSource();
  private unmTraceSource = new VectorSource();
  private unmLayer?: VectorLayer<VectorSource>;
  private cadastralSource = new VectorSource();
  private cadastralLayer?: VectorLayer<VectorSource>;
  private cadastralDrawSource = new VectorSource();
  private cadastralDraw?: Draw;

  cadastralSelectedParcelId: number | null = null;
  cadastralDrawnGeometry: GeoJSON.Polygon | null = null;
  cadastralDrawActive = false;

  assetDetailOpen = false;
  assetDetailKind: 'highway' | 'utility' = 'highway';
  selectedAssetId: number | null = null;
  clusterPickCandidates: { id: number; label: string; type: string; kind: 'highway' | 'utility' }[] | null =
    null;

  editLabel = '';
  editEventType = '';
  editUtilityType = '';
  editMeasureKm = 0;
  editMeasureToKm = 0;
  editSide = '';
  editConditionGrade = '';
  editAssetRef = '';
  editLateralOffsetM = 0;
  editUtilityDepthM: number | null = null;
  editUtilityDiameterMm: number | null = null;
  editGrade: LrsGrade = 'overground';
  savingAsset = false;

  private draw?: Draw;
  private realignDraw?: Draw;
  private routeModify?: Modify;
  private routeSnap?: Snap;
  private clickKey?: EventsKey;
  private resolutionKey?: EventsKey;
  private pointClusterLayer?: VectorLayer<VectorSource>;
  private utilityClusterLayer?: VectorLayer<VectorSource>;
  private linearEventLayer?: VectorLayer<VectorSource>;
  private geoJson = new GeoJSON();
  private resizeObserver?: ResizeObserver;
  private attachedLayers: Layer[] = [];
  private mapBridgeSub?: Subscription;
  private dashboardSub?: Subscription;

  constructor(
    private lrs: LrsService,
    private unm: UnmService,
    private api: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private geoBridge: GeospatialMapBridgeService,
    private dashboard: LrsDashboardService,
    private rbac: RbacService
  ) {}

  /** Mirrors the server's LRS canEdit guard: 'gis assets' add or update. */
  get cadastralCanEdit(): boolean {
    return this.rbac.hasPermission('gis assets', 'add') || this.rbac.hasPermission('gis assets', 'update');
  }

  get selectedAssetLabel(): string {
    return getLrsAssetTypeMeta(this.newEventType)?.label || this.newEventType;
  }

  get unmPropertyFields() {
    return UNM_ASSET_PROPERTY_FIELDS[this.unmAssetType] || [];
  }

  get unmSelectedDomainTiers() {
    return this.unmDomains.find((d) => d.domain_id === this.unmDomainId)?.tiers || [];
  }

  get selectedAssetIsLinear(): boolean {
    return isLinearAssetType(this.newEventType);
  }

  get assetLabelPlaceholder(): string {
    return defaultAssetLabel(this.newEventType, this.measureKm, this.measureToKm || undefined);
  }

  get filteredAssetEvents(): LrsEvent[] {
    const q = this.normalizedAssetSearch;
    if (!q) return this.assetEvents;
    return this.assetEvents.filter((e) => this.eventMatchesSearch(e, q));
  }

  get assetEventsByClass(): { classLabel: string; items: LrsEvent[] }[] {
    const groups = {} as { [k: string]: LrsEvent[] };
    for (const e of this.filteredAssetEvents) {
      const key = e.event_type || 'other';
      (groups[key] || (groups[key] = [])).push(e);
    }
    const types = Object.keys(groups).sort((a, b) => a.localeCompare(b));
    const result: { classLabel: string; items: LrsEvent[] }[] = [];
    for (let i = 0; i < types.length; i++) {
      const type = types[i];
      result.push({
        classLabel: getLrsAssetTypeMeta(type)?.label || type,
        items: groups[type],
      });
    }
    return result;
  }

  get filteredUtilityAssets(): LrsUtilityAsset[] {
    const q = this.normalizedAssetSearch;
    if (!q) return this.utilityAssets;
    return this.utilityAssets.filter((u) => this.utilityMatchesSearch(u, q));
  }

  get assetSearchHits(): LrsAssetSearchHit[] {
    const q = this.normalizedAssetSearch;
    if (!q) return [];
    const hits: LrsAssetSearchHit[] = [
      ...this.filteredAssetEvents.map((e) => ({
        kind: 'highway' as const,
        id: e.id,
        label: e.label || e.event_type,
        type: e.event_type,
        chainage: e.chainage_label || formatChainageFromMeters(e.measure_m),
      })),
      ...this.filteredUtilityAssets.map((u) => ({
        kind: 'utility' as const,
        id: u.id,
        label: u.label || u.utility_type,
        type: u.utility_type,
        chainage: formatChainageFromMeters(u.measure_m),
      })),
    ];
    return hits.slice(0, 40);
  }

  get placeAssetHint(): string {
    if (!this.placeAssetMode) return '';
    if (this.selectedAssetIsLinear) {
      return this.chainagePickTarget === 'to'
        ? `Click the route to set the end of ${this.selectedAssetLabel}, then Save asset.`
        : `Click the route to set the start of ${this.selectedAssetLabel}, then click again for the end.`;
    }
    return `Click the route to place ${this.selectedAssetLabel}, then click Save asset.`;
  }

  private get normalizedAssetSearch(): string {
    return this.assetSearchQuery.trim().toLowerCase();
  }

  private getRouteMaxMeasureKm(): number | null {
    if (!this.selectedRoute?.length_m) return null;
    const originKm = (this.selectedRoute.origin_measure_m || 0) / 1000;
    return originKm + this.selectedRoute.length_m / 1000;
  }

  /** Returns an error message when chainage range is invalid; null when OK. */
  validateChainageRange(fromKm: number, toKm: number | null | undefined, isLinear: boolean): string | null {
    if (!Number.isFinite(fromKm) || fromKm < 0) {
      return 'From chainage must be a valid km value ≥ 0.';
    }
    const maxKm = this.getRouteMaxMeasureKm();
    if (maxKm != null && fromKm > maxKm + 0.001) {
      return `From chainage ${fromKm.toFixed(3)} km exceeds route end (${maxKm.toFixed(3)} km).`;
    }
    if (!isLinear) return null;
    if (toKm == null || !Number.isFinite(toKm)) {
      return 'Linear assets require a valid "to" chainage.';
    }
    if (toKm <= fromKm) {
      return `"To" chainage (${toKm.toFixed(3)} km) must be greater than "from" (${fromKm.toFixed(3)} km).`;
    }
    if (maxKm != null && toKm > maxKm + 0.001) {
      return `To chainage ${toKm.toFixed(3)} km exceeds route end (${maxKm.toFixed(3)} km).`;
    }
    return null;
  }

  private refreshChainageValidation(
    fromKm: number,
    toKm: number | null | undefined,
    isLinear: boolean
  ): void {
    this.chainageValidationError = this.validateChainageRange(fromKm, toKm, isLinear) || '';
  }

  onMeasureKmChange(): void {
    this.refreshChainageValidation(this.measureKm, this.selectedAssetIsLinear ? this.measureToKm : null, this.selectedAssetIsLinear);
  }

  onMeasureToKmChange(): void {
    this.refreshChainageValidation(this.measureKm, this.measureToKm, true);
  }

  onEditMeasureKmChange(): void {
    this.refreshChainageValidation(this.editMeasureKm, this.editAssetIsLinear ? this.editMeasureToKm : null, this.editAssetIsLinear);
  }

  onEditMeasureToKmChange(): void {
    this.refreshChainageValidation(this.editMeasureKm, this.editMeasureToKm, true);
  }

  startChainagePick(target: 'from' | 'to'): void {
    if (!this.selectedRouteId) {
      this.statusMessage = 'Select a route first';
      return;
    }
    this.chainagePickTarget = target;
    this.chainagePickActive = true;
    this.statusMessage =
      target === 'to'
        ? 'Click the route on the map to set "to" chainage'
        : 'Click the route on the map to set "from" chainage';
  }

  togglePlaceAssetMode(): void {
    if (this.placeAssetMode) {
      this.exitPlaceAssetMode();
      return;
    }
    if (!this.selectedRouteId) {
      this.statusMessage = 'Select a route first';
      return;
    }
    if (this.assetsLocked) {
      this.statusMessage = 'Assets are locked until Map → Road → LRS all pass.';
      return;
    }
    this.placeAssetMode = true;
    this.assetDetailOpen = false;
    this.chainagePickTarget = 'from';
    this.chainagePickActive = true;
    this.statusMessage = this.placeAssetHint;
  }

  private exitPlaceAssetMode(announce = true): void {
    if (!this.placeAssetMode) return;
    this.placeAssetMode = false;
    this.chainagePickActive = false;
    this.chainagePickTarget = 'from';
    if (announce) this.statusMessage = 'Place mode off';
  }

  onAssetSearchChange(): void {
    if (this.searchPanTimer) {
      clearTimeout(this.searchPanTimer);
      this.searchPanTimer = null;
    }
    const q = this.assetSearchQuery.trim();
    if (!this.looksLikeChainageQuery(q) || !this.selectedRouteId) return;
    const meters = parseChainageMeters(q);
    if (meters == null) return;
    this.searchPanTimer = setTimeout(() => this.panToSearchChainage(meters), 350);
  }

  openAssetSearchHit(hit: LrsAssetSearchHit): void {
    if (hit.kind === 'utility') {
      const u = this.utilityAssets.find((x) => x.id === hit.id);
      if (u) this.openUtilityAssetDetail(u);
      return;
    }
    const e = this.assetEvents.find((x) => x.id === hit.id);
    if (e) this.openHighwayAssetDetail(e);
  }

  openUtilityFromList(asset: LrsUtilityAsset): void {
    this.openUtilityAssetDetail(asset);
  }

  private looksLikeChainageQuery(raw: string): boolean {
    const s = raw.trim();
    if (!s) return false;
    if (/^(?:ch\s*)?\d+\+\d/i.test(s)) return true;
    if (/\bkm\s*[\d.]+/i.test(s)) return true;
    if (/^\d+\.\d+$/.test(s)) return true;
    return false;
  }

  private eventMatchesSearch(e: LrsEvent, q: string): boolean {
    const props = e.properties || {};
    const hay = [
      e.label,
      e.event_type,
      e.chainage_label,
      e.chainage_to_label,
      e.side,
      props['asset_ref'],
      e.id,
      getLrsAssetTypeMeta(e.event_type)?.label,
    ]
      .map((v) => String(v ?? ''))
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  }

  private utilityMatchesSearch(u: LrsUtilityAsset, q: string): boolean {
    const props = u.properties || {};
    const hay = [
      u.label,
      u.utility_type,
      u.side,
      u.grade,
      formatChainageFromMeters(u.measure_m),
      u.measure_to_m != null ? formatChainageFromMeters(u.measure_to_m) : '',
      props['asset_ref'],
      u.id,
      LRS_UTILITY_TYPES.find((t) => t.id === u.utility_type)?.label,
    ]
      .map((v) => String(v ?? ''))
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  }

  private panToSearchChainage(meters: number): void {
    if (!this.selectedRouteId) return;
    this.measureKm = meters / 1000;
    this.onMeasureKmChange();
    const label = formatChainageFromMeters(meters);
    this.dashboard.seekStation({ measure_m: meters, label });
    this.lrs.interpolate(this.selectedRouteId, this.measureKm).subscribe({
      next: (res) => {
        this.locateResult = res.data;
        const pt = res.data.closest_point || res.data.point;
        if (pt?.coordinates) {
          const [lng, lat] = pt.coordinates as [number, number];
          this.showMarker(lng, lat, res.data.chainage_label || label);
          this.fitToSource(this.markerSource, 17);
        }
        this.loadBoqAtChainage();
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Interpolate failed'),
    });
  }

  ngOnInit(): void {
    if (this.sidebarPanelOnly) this.restoreSidebarLayout();
    if (this.sidebarPanelOnly) {
      this.integratedMode = true;
      this.embedded = true;
    }
    this.integratedMode =
      this.integratedMode || this.route.snapshot.data['geoShellIntegrated'] === true;
    this.embedded = this.integratedMode || this.route.snapshot.data['geoShellEmbedded'] === true;
    this.projectId = this.route.snapshot.queryParamMap.get('project_id') || '';
    const routeIdParam = this.route.snapshot.queryParamMap.get('route_id');
    this.pendingRouteId = routeIdParam ? parseInt(routeIdParam, 10) : null;
    const fromKmParam = this.route.snapshot.queryParamMap.get('from_km');
    const toKmParam = this.route.snapshot.queryParamMap.get('to_km');
    if (fromKmParam != null && fromKmParam !== '' && Number.isFinite(parseFloat(fromKmParam))) {
      this.segFromKm = parseFloat(fromKmParam);
    }
    if (toKmParam != null && toKmParam !== '' && Number.isFinite(parseFloat(toKmParam))) {
      this.segToKm = parseFloat(toKmParam);
    }
    this.roadProjectImportId = this.projectId;
    const demoParam = this.route.snapshot.queryParamMap.get('demo');
    if (demoParam === 'lrs' || demoParam === 'showcase') {
      this.pendingRouteId = null;
    }
    this.loadRoutes(this.projectId || undefined);
    this.loadNetworks();
    this.loadEventTypeCatalog();
    this.querySub = this.route.queryParamMap.subscribe((qp) => {
      this.maybeStartLrsDemo(qp);
      this.syncDocumentFromQuery(qp);
    });
    this.lrs.health().subscribe({
      next: () => (this.statusMessage = 'LRS ready — route + measure model'),
      error: (e) => (this.mapError = e?.error?.error || 'LRS health check failed'),
    });
    this.pendingOfflineCount = this.readOfflineQueue().length;
    window.addEventListener('online', this.onlineListener);
    if (navigator.onLine && this.pendingOfflineCount > 0) {
      setTimeout(() => this.syncOfflineQueue(), 2000);
    }
    this.dashboardSub = this.dashboard.actions$.subscribe((action) => this.handleDashboardAction(action));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapInteractActive'] && this.integratedMode) {
      if (!this.mapInteractActive) {
        this.chainagePickActive = false;
        if (this.drawMode) this.toggleDrawRoute();
      } else if (this.placeAssetMode) {
        this.chainagePickActive = true;
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.integratedMode) {
      this.attachToGisMap();
    } else {
      this.initMap();
    }
    preloadLrsIcons().then(() => {
      this.refreshMapSymbolLayers();
      if (this.routeSource.getFeatures().length) {
        this.fitToSource(this.routeSource);
      }
    });
  }

  /** Re-style layers after SVG data URLs are ready. */
  private refreshMapSymbolLayers(): void {
    clearLrsIconStyleCache();
    this.pointClusterLayer?.changed();
    this.linearEventLayer?.changed();
    this.utilityClusterLayer?.changed();
    this.map?.render();
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.onlineListener);
    this.querySub?.unsubscribe();
    this.mapBridgeSub?.unsubscribe();
    this.dashboardSub?.unsubscribe();
    if (this.clickKey) unByKey(this.clickKey);
    if (this.resolutionKey) unByKey(this.resolutionKey);
    if (this.moveEndKey) unByKey(this.moveEndKey);
    if (this.clusterReloadTimer) clearTimeout(this.clusterReloadTimer);
    if (this.searchPanTimer) clearTimeout(this.searchPanTimer);
    if (this.clusterDistanceRaf) cancelAnimationFrame(this.clusterDistanceRaf);
    this.resizeObserver?.disconnect();
    this.draw?.setActive(false);
    this.realignDraw?.setActive(false);
    this.stopCadastralDraw();
    this.teardownRouteModify();
    if (this.integratedMode && this.map) {
      this.attachedLayers.forEach((layer) => this.map?.removeLayer(layer));
      this.attachedLayers = [];
      if (this.geoserverLayerRef) {
        this.map.removeLayer(this.geoserverLayerRef);
        this.geoserverLayerRef = undefined;
      }
      this.map = undefined;
    } else {
      this.map?.setTarget(undefined);
      this.map = undefined;
    }
  }

  assetColor(eventType: string): string {
    return getLrsAssetTypeMeta(eventType)?.color || '#ea580c';
  }

  assetIconPath(assetType: string): string {
    const meta = getLrsAssetTypeMeta(assetType);
    if (meta?.iconPath) return meta.iconPath;
    const util = LRS_UTILITY_TYPES.find((u) => u.id === assetType);
    if (util?.iconPath) return util.iconPath;
    return iconPathForAssetType(assetType);
  }

  onClusterSettingsChange(): void {
    if (this.clusterEnabled) {
      this.applyZoomClusterDistance();
      this.pointClusterLayer?.setSource(this.pointEventClusterSource);
      this.utilityClusterLayer?.setSource(this.utilityPointClusterSource);
    } else {
      this.pointClusterLayer?.setSource(this.pointEventRawSource);
      this.utilityClusterLayer?.setSource(this.utilityPointRawSource);
    }
    this.pointClusterLayer?.changed();
    this.utilityClusterLayer?.changed();
  }

  private applyZoomClusterDistance(): void {
    if (!this.clusterEnabled) return;
    const zoom = this.map?.getView().getZoom() ?? 0;
    const dist = clusterDistanceForZoom(zoom, this.clusterDistancePx);
    this.pointEventClusterSource.setDistance(dist);
    this.pointEventClusterSource.setMinDistance(dist > 0 ? Math.min(16, dist) : 0);
    const utilBase = Math.max(28, this.clusterDistancePx - 8);
    const utilDist = clusterDistanceForZoom(zoom, utilBase);
    this.utilityPointClusterSource.setDistance(utilDist);
    this.utilityPointClusterSource.setMinDistance(utilDist > 0 ? Math.min(14, utilDist) : 0);
  }

  /** TC-14: disable clustering and MVT so symbols sit on stored coordinates. */
  enableTruePositionMode(): void {
    this.clusterEnabled = false;
    this.onClusterSettingsChange();
    if (this.serverClusterMode) this.toggleServerCluster();
    if (this.mvtMode) this.toggleMvtLayer();
    this.statusMessage = 'True position on — clusters and MVT off; symbols use stored coordinates';
  }

  get truePositionMode(): boolean {
    return !this.clusterEnabled && !this.serverClusterMode && !this.mvtMode;
  }

  /** Toggle PostGIS ST_ClusterDBSCAN server-side clustering (scales to millions). */
  toggleServerCluster(): void {
    this.serverClusterMode = !this.serverClusterMode;
    const clientPointVisible = !this.serverClusterMode;
    this.pointClusterLayer?.setVisible(clientPointVisible);
    this.utilityClusterLayer?.setVisible(clientPointVisible);
    this.serverClusterLayer?.setVisible(this.serverClusterMode);
    if (this.serverClusterMode) {
      this.loadServerClusters();
    } else {
      this.serverClusterSource.clear();
      this.serverClusterMeta = null;
    }
  }

  private scheduleServerClusterReload(): void {
    if (this.clusterReloadTimer) clearTimeout(this.clusterReloadTimer);
    this.clusterReloadTimer = setTimeout(() => this.loadServerClusters(), 250);
  }

  private loadServerClusters(): void {
    if (!this.map || !this.serverClusterMode) return;
    const view = this.map.getView();
    const size = this.map.getSize();
    if (!size) return;
    const extent3857 = view.calculateExtent(size);
    const [minLng, minLat] = toLonLat([extent3857[0], extent3857[1]]);
    const [maxLng, maxLat] = toLonLat([extent3857[2], extent3857[3]]);
    const zoom = Math.round(view.getZoom() ?? 12);

    const method = this.serverClusterMethod;
    const useUtilities = method === 'utility_network';

    this.lrs
      .clusterEvents(this.selectedRouteId ?? null, {
        method,
        zoom,
        radius_px: this.clusterDistancePx,
        bbox: { minLng, minLat, maxLng, maxLat },
        event_type: useUtilities ? undefined : this.assetFilterType || undefined,
      })
      .subscribe({
        next: (res) => {
          this.serverClusterSource.clear();
          const feats = this.geoJson.readFeatures(res.data, {
            featureProjection: 'EPSG:3857',
          });
          this.serverClusterSource.addFeatures(feats);
          this.serverClusterMeta = {
            method: res.data.meta.method || method,
            cluster_count: res.data.meta.cluster_count,
            singleton_count: res.data.meta.singleton_count,
            eps_m: res.data.meta.eps_m != null ? Math.round(res.data.meta.eps_m) : undefined,
            cell_m: res.data.meta.cell_m != null ? Math.round(res.data.meta.cell_m) : undefined,
            total_groups: res.data.meta.total_groups,
          };
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Server clustering failed'),
      });
  }

  /** Switch the server clustering strategy and reload the current viewport. */
  setClusterMethod(method: 'dbscan' | 'grid' | 'heatmap' | 'utility_network'): void {
    this.serverClusterMethod = method;
    if (this.serverClusterMode) this.loadServerClusters();
  }

  private styleServerCluster(feature: FeatureLike): Style {
    const method = this.serverClusterMethod;

    // Heatmap: graduated translucent blobs by relative weight.
    if (method === 'heatmap') {
      const weight = Number(feature.get('weight')) || 0.1;
      const count = Number(feature.get('count')) || 1;
      const radius = 10 + weight * 34;
      const alpha = 0.25 + weight * 0.5;
      return new Style({
        image: new CircleStyle({
          radius,
          fill: new Fill({ color: `rgba(239, 68, 68, ${alpha.toFixed(2)})` }),
        }),
        text:
          count > 1
            ? new OlText({ text: String(count), fill: new Fill({ color: '#7f1d1d' }), font: 'bold 10px sans-serif' })
            : undefined,
      });
    }

    const isCluster = feature.get('cluster') === true;
    if (isCluster) {
      const count = Number(feature.get('count')) || 0;
      const radius = Math.min(28, 12 + Math.log10(count + 1) * 9);
      const fill =
        method === 'utility_network'
          ? this.utilityTypeColor(String(feature.get('utility_type') || ''))
          : 'rgba(37, 99, 235, 0.85)';
      return new Style({
        image: new CircleStyle({
          radius,
          fill: new Fill({ color: fill }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
        }),
        text: new OlText({
          text: count >= 1000 ? `${(count / 1000).toFixed(1)}k` : String(count),
          fill: new Fill({ color: '#fff' }),
          font: 'bold 11px sans-serif',
        }),
      });
    }
    const color =
      method === 'utility_network'
        ? this.utilityTypeColor(String(feature.get('utility_type') || ''))
        : this.assetColor(String(feature.get('event_type') || ''));
    return new Style({
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#fff', width: 1.5 }),
      }),
    });
  }

  private utilityTypeColor(type: string): string {
    const palette: Record<string, string> = {
      water: '#0ea5e9',
      sewer: '#92400e',
      storm_drain: '#0891b2',
      electric_ht: '#dc2626',
      electric_lt: '#f97316',
      ofc: '#7c3aed',
      gas: '#eab308',
      telecom: '#16a34a',
    };
    return palette[type] || '#6366f1';
  }

  /** Toggle per-lane geometry layer (merge/diverge/weave ribbons). */
  toggleLanes(): void {
    if (!this.map) return;
    this.lanesMode = !this.lanesMode;
    this.lanesLayer?.setVisible(this.lanesMode);
    if (this.lanesMode) {
      this.loadLanes();
    } else {
      this.lanesSource.clear();
    }
  }

  private loadLanes(): void {
    if (this.selectedRouteId == null) {
      this.statusMessage = 'Select a route to view lanes';
      return;
    }
    this.lrs.listLanes(this.selectedRouteId).subscribe({
      next: (res) => {
        this.lanes = res.data || [];
        this.lanesSource.clear();
        for (const lane of this.lanes) {
          if (!lane.geometry) continue;
          const feats = this.geoJson.readFeatures(
            {
              type: 'Feature',
              geometry: lane.geometry,
              properties: {
                lane_code: lane.lane_code,
                transition_type: lane.transition_type,
                lane_type: lane.lane_type,
              },
            },
            { featureProjection: 'EPSG:3857' }
          );
          this.lanesSource.addFeatures(feats);
        }
      },
      error: () => (this.lanes = []),
    });
  }

  /** Auto-generate lanes for the first carriageway, then show them. */
  generateLanesForCarriageway(): void {
    if (this.selectedRouteId == null) return;
    const cw = this.carriageways[0];
    if (!cw) {
      this.statusMessage = 'Add a carriageway first, then generate lanes';
      return;
    }
    this.lrs.generateLanes(this.selectedRouteId, cw.id, { replace: true }).subscribe({
      next: (res) => {
        this.statusMessage = `Generated ${res.data.lane_count} lanes for ${cw.carriageway_code}`;
        this.lanesMode = true;
        this.lanesLayer?.setVisible(true);
        this.loadLanes();
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Lane generation failed'),
    });
  }

  private styleLane(feature: FeatureLike): Style {
    const transition = String(feature.get('transition_type') || 'through');
    const colors: Record<string, string> = {
      through: '#0ea5e9',
      merge: '#16a34a',
      diverge: '#f59e0b',
      weave: '#a855f7',
    };
    return new Style({
      stroke: new Stroke({
        color: colors[transition] || '#0ea5e9',
        width: 2.5,
        lineDash: transition === 'through' ? undefined : [8, 5],
      }),
    });
  }

  /** Toggle the MVT vector-tile layer (ST_AsMVT server-streamed tiles). */
  toggleMvtLayer(): void {
    if (!this.map) return;
    this.mvtMode = !this.mvtMode;
    if (this.mvtMode) {
      if (!this.mvtLayer) this.mvtLayer = this.buildMvtLayer();
      else this.mvtLayer.setSource(this.buildMvtSource());
      this.map.addLayer(this.mvtLayer);
      this.statusMessage = 'MVT vector tiles on (server-streamed)';
    } else if (this.mvtLayer) {
      this.map.removeLayer(this.mvtLayer);
    }
  }

  private authToken(): string {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user)?.token || '' : '';
    } catch {
      return '';
    }
  }

  private buildMvtSource(): VectorTileSource {
    const template = this.lrs.vectorTileUrlTemplate(this.selectedRouteId ?? null);
    const token = this.authToken();
    return new VectorTileSource({
      format: new MVT({ layerName: 'mvt_layer' }),
      url: template,
      tileLoadFunction: (tile, url) => {
        const vt = tile as unknown as {
          setLoader: (
            loader: (extent: number[], resolution: number, projection: unknown) => void
          ) => void;
          getFormat: () => MVT;
          setFeatures: (features: unknown[]) => void;
        };
        vt.setLoader((extent, _resolution, projection) => {
          fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
            .then((r) => (r.status === 204 ? null : r.arrayBuffer()))
            .then((buf) => {
              if (!buf) {
                vt.setFeatures([]);
                return;
              }
              const features = vt
                .getFormat()
                .readFeatures(buf, { extent, featureProjection: projection as never });
              vt.setFeatures(features as unknown[]);
            })
            .catch(() => vt.setFeatures([]));
        });
      },
    });
  }

  private buildMvtLayer(): VectorTileLayer {
    return new VectorTileLayer({
      source: this.buildMvtSource(),
      zIndex: 9,
      style: (feature) => {
        const layerName = feature.get('mvt_layer');
        if (layerName === 'routes') {
          return new Style({ stroke: new Stroke({ color: '#7c3aed', width: 3 }) });
        }
        if (layerName === 'utilities') {
          return new Style({
            image: new CircleStyle({
              radius: 4,
              fill: new Fill({ color: '#0ea5e9' }),
              stroke: new Stroke({ color: '#fff', width: 1 }),
            }),
          });
        }
        const color = this.assetColor(String(feature.get('event_type') || ''));
        return new Style({
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#fff', width: 1 }),
          }),
        });
      },
    });
  }

  private addHighwayEventFeature(f: Feature, props?: Record<string, unknown>): void {
    applySymbolProperties(f, props);
    f.set('asset_kind', 'highway');
    if (!this.isCatalogVisible(f)) return;
    if (f.get('is_linear') || f.get('measure_to_m')) {
      this.linearEventSource.addFeature(f);
    } else {
      this.pointEventRawSource.addFeature(f);
    }
  }

  isCatalogVisible(f: Feature): boolean {
    const props: CatalogFeatureProps = {
      event_type: f.get('event_type'),
      utility_type: f.get('utility_type'),
      grade: f.get('grade'),
      depth_m: f.get('depth_m'),
      lrsKind: f.get('lrsKind'),
    };
    return isCatalogFeatureVisible(props, this.nhaiLayerVisibility);
  }

  onNhaiVisibility(ev: { nodeId: string; checked: boolean }): void {
    this.nhaiLayerVisibility = toggleCatalogNode(ev.nodeId, ev.checked, this.nhaiLayerVisibility);
    this.rebuildEventLayersFromList();
    this.refreshUtilityFromList(this.utilityAssets);
    this.chainageLayer?.setVisible(this.nhaiLayerVisibility['ref-chainage'] !== false);
    this.lanesLayer?.setVisible(this.nhaiLayerVisibility['road-corridor'] !== false);
    this.carriagewayLayer?.setVisible(this.nhaiLayerVisibility['road-corridor'] !== false);
    this.chainageLayer?.setVisible(this.nhaiLayerVisibility['ref-chainage'] !== false);
    this.refreshNhaiCounts();
  }

  private refreshNhaiCounts(): void {
    const features: CatalogFeatureProps[] = this.assetEvents.map((e) => ({
      event_type: e.event_type,
      grade: e.grade,
      depth_m: (e as { depth_m?: number }).depth_m,
    }));
    for (const u of this.utilityAssets) {
      features.push({
        utility_type: u.utility_type,
        grade: u.grade,
        depth_m: u.depth_m,
      });
    }
    const counts: Record<string, number> = {};
    for (const leaf of flattenCatalogLeaves(false)) {
      counts[leaf.id] = countFeaturesForLeaf(leaf, features);
    }
    this.nhaiLayerCounts = counts;
  }

  runLocateAtEdge(): void {
    if (!this.selectedRouteId) return;
    this.locateStatus = 'Searching…';
    this.lrs
      .locateAssetsAtOffset(this.selectedRouteId, {
        chainage: this.locateChainage || undefined,
        measure_km: !this.locateChainage ? this.measureKm : undefined,
        side: this.locateSide,
        edge_offset_m: this.locateEdgeOffsetM ?? undefined,
      })
      .subscribe({
        next: (res) => {
          this.locateResults = res.data?.items || [];
          this.locateStatus = `${res.data?.count || 0} asset(s) at ${res.data?.chainage_label || this.locateChainage}`;
        },
        error: (e) => {
          this.locateStatus = e?.error?.error || 'Locate failed';
          this.locateResults = [];
        },
      });
  }

  private clearHighwayEventSources(): void {
    this.pointEventRawSource.clear();
    this.linearEventSource.clear();
  }

  nearbyDistanceM(item: Record<string, unknown>): number {
    const d = item['distance_along_m'];
    return typeof d === 'number' ? d : parseFloat(String(d)) || 0;
  }

  selectAssetType(id: string): void {
    this.newEventType = id;
    this.highwayGrade = defaultGradeForEventType(id);
    const meta = getLrsAssetTypeMeta(id);
    if (meta?.geometryKind === 'linear') {
      const spanKm = (meta.defaultSpanM || 100) / 1000;
      if (this.measureToKm <= this.measureKm) {
        this.measureToKm = this.measureKm + spanKm;
      }
    }
    if (!this.newEventLabel.trim()) {
      this.newEventLabel = defaultAssetLabel(id, this.measureKm, this.measureToKm || undefined);
    }
    this.onMeasureKmChange();
    if (this.placeAssetMode) {
      this.chainagePickTarget = 'from';
      this.chainagePickActive = true;
      this.statusMessage = this.placeAssetHint;
    }
  }

  selectUtilityType(id: string): void {
    this.utilityType = id;
    this.utilityGrade = defaultGradeForUtilityType(id);
  }

  get createExtraFields() {
    const kind = this.activePanel === 'utilities' ? 'utility' : 'event';
    const typeId = kind === 'utility' ? this.utilityType : this.newEventType;
    const grade = kind === 'utility' ? this.utilityGrade : this.highwayGrade;
    return specFieldsForAsset({ kind, typeId, grade }).filter(
      (f) => f.store === 'properties' && f.kind !== 'readonly'
    );
  }

  openInGisMvp(): void {
    const q: Record<string, string> = {};
    if (this.projectId) q['project_id'] = this.projectId;
      this.router.navigate(['/home/geospatial'], { queryParams: q });
  }

  loadDemoShowcase(): void {
    this.seedingDemo = true;
    this.mapError = '';
    this.demoWalkthroughPending = true;
    this.lrs.seedDemo({ project_id: this.projectId || 'LRS-DEMO', replace: true }).subscribe({
      next: (res) => {
        this.seedingDemo = false;
        this.demoSeedSummary = res.data;
        this.statusMessage = `Demo loaded: ${res.data.route_code} (${res.data.length_km.toFixed(2)} km)`;
        this.segFromKm = 1.9;
        this.segToKm = Math.min(2.15, res.data.length_km - 0.1);
        this.pendingRouteId = res.data.route_id;
        this.selectedRouteId = null;
        this.loadRoutes(this.projectId || undefined);
        setTimeout(() => {
          if (this.map && this.routeSource.getFeatures().length) {
            this.fitToSource(this.routeSource);
          }
          preloadLrsIcons().then(() => this.refreshMapSymbolLayers());
        }, 400);
      },
      error: (e) => {
        this.seedingDemo = false;
        this.demoWalkthroughPending = false;
        this.statusMessage = e?.error?.error || 'Demo seed failed';
        this.mapError = this.statusMessage;
      },
    });
  }

  private maybeStartLrsDemo(qp: ParamMap): void {
    const demo = qp.get('demo');
    if (demo !== 'lrs' && demo !== 'showcase') return;
    const run = qp.get('demo_run') || 'default';
    if (this.seedingDemo) return;
    if (this.demoAutoStarted && this.lastDemoRun === run) return;
    this.demoAutoStarted = true;
    this.lastDemoRun = run;
    this.loadDemoShowcase();
  }

  private startDemoWalkthrough(): void {
    if (!this.selectedRouteId) return;
    if (this.selectedRoute?.network_id) this.selectNetwork(this.selectedRoute.network_id);
    this.segFromKm = 1.9;
    this.segToKm = Math.min(2.15, Math.max(0.2, ((this.selectedRoute?.length_m || 5800) / 1000) - 0.1));
    this.setActivePanel('utilities');
    this.loadCorridorInventory();
    this.runRouteQc();
    this.runNetworkQc();
    const code = this.selectedRoute?.route_code || 'NH-DEMO-48';
    const lenKm = ((this.selectedRoute?.length_m || 0) / 1000).toFixed(2);
    this.statusMessage =
      `Demo spine: network → ${code} (${lenKm} km). Grade band ${this.segFromKm}–${this.segToKm} km ` +
      `(UG water, OG HT, XO underpass). Map / Road / LRS gates below.`;
  }

  loadRoutes(projectId?: string): void {
    this.loading = true;
    this.lrs.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.routes = res.data || [];
        this.loading = false;
        const targetId =
          this.pendingRouteId != null && this.routes.some((r) => r.id === this.pendingRouteId)
            ? this.pendingRouteId
            : null;
        if (targetId != null) {
          this.selectRoute(targetId);
          this.pendingRouteId = null;
        } else if (this.routes.length && !this.selectedRouteId) {
          this.selectRoute(this.routes[0].id);
        }
      },
      error: (e) => {
        this.loading = false;
        this.mapError = e?.error?.error || 'Failed to load routes';
      },
    });
  }

  loadNetworks(): void {
    this.lrs.listNetworks(this.projectId ? { project_id: this.projectId } : undefined).subscribe({
      next: (res) => {
        this.networks = res.data || [];
        if (this.networks.length && this.selectedNetworkId == null) {
          this.selectNetwork(this.networks[0].id);
        }
      },
      error: () => (this.networks = []),
    });
  }

  selectNetwork(id: number | null): void {
    if (!id) {
      this.selectedNetworkId = null;
      this.selectedSubnetworkId = null;
      this.subnetworks = [];
      this.editNetworkRegion = '';
      this.editNetworkCircle = '';
      this.editNetworkDivision = '';
      this.editNetworkSubDivision = '';
      return;
    }
    this.selectedNetworkId = id;
    this.selectedSubnetworkId = null;
    const net = this.networks.find((n) => n.id === id);
    this.editNetworkRegion = net?.region || '';
    this.editNetworkCircle = net?.circle || '';
    this.editNetworkDivision = net?.division || '';
    this.editNetworkSubDivision = net?.sub_division || '';
    this.lrs.listSubnetworks(id).subscribe({
      next: (res) => {
        this.subnetworks = res.data || [];
        if (this.subnetworks.length) this.selectedSubnetworkId = this.subnetworks[0].id;
      },
      error: () => (this.subnetworks = []),
    });
  }

  createNetwork(): void {
    const code = this.newNetworkCode.trim();
    const name = this.newNetworkName.trim() || code;
    if (!code) {
      this.statusMessage = 'Enter a network code';
      return;
    }
    this.lrs
      .createNetwork({
        network_code: code,
        network_name: name,
        project_id: this.projectId || undefined,
        region: this.newNetworkRegion.trim() || undefined,
        circle: this.newNetworkCircle.trim() || undefined,
        division: this.newNetworkDivision.trim() || undefined,
        sub_division: this.newNetworkSubDivision.trim() || undefined,
      })
      .subscribe({
        next: (res) => {
          this.statusMessage = `Network ${res.data.network_code} created`;
          this.newNetworkCode = '';
          this.newNetworkName = '';
          this.newNetworkRegion = '';
          this.newNetworkCircle = '';
          this.newNetworkDivision = '';
          this.newNetworkSubDivision = '';
          this.selectedNetworkId = res.data.id;
          this.loadNetworks();
          this.selectNetwork(res.data.id);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Create network failed'),
      });
  }

  saveNetworkHierarchy(): void {
    if (!this.selectedNetworkId) return;
    this.lrs
      .updateNetwork(this.selectedNetworkId, {
        region: this.editNetworkRegion.trim() || null,
        circle: this.editNetworkCircle.trim() || null,
        division: this.editNetworkDivision.trim() || null,
        sub_division: this.editNetworkSubDivision.trim() || null,
      })
      .subscribe({
        next: () => {
          this.statusMessage = 'Network hierarchy saved';
          this.loadNetworks();
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Save hierarchy failed'),
      });
  }

  createSubnetwork(): void {
    if (!this.selectedNetworkId) {
      this.statusMessage = 'Create a road network first';
      return;
    }
    const code = this.newSubnetworkCode.trim();
    const name = this.newSubnetworkName.trim() || code;
    if (!code) {
      this.statusMessage = 'Enter a sub-network code';
      return;
    }
    this.lrs
      .createSubnetwork(this.selectedNetworkId, {
        subnetwork_code: code,
        subnetwork_name: name,
      })
      .subscribe({
        next: (res) => {
          this.statusMessage = `Sub-network ${res.data.subnetwork_code} created`;
          this.newSubnetworkCode = '';
          this.newSubnetworkName = '';
          this.selectNetwork(this.selectedNetworkId!);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Create sub-network failed'),
      });
  }

  applyNetworkRepairs(): void {
    this.applyingRepairs = true;
    this.lrs
      .applyNetworkRepairs({
        project_id: this.projectId || undefined,
        route_id: this.selectedRouteId || undefined,
        snap_m: 5,
      })
      .subscribe({
        next: (res) => {
          this.applyingRepairs = false;
          this.networkQcReport = res.data.after;
          this.statusMessage = `Applied ${res.data.applied?.length || 0} repair(s)`;
          this.applyNetworkQcOverlay(res.data.after);
          if (this.selectedRouteId) this.runRouteQc();
        },
        error: (e) => {
          this.applyingRepairs = false;
          this.statusMessage = e?.error?.error || 'Apply repairs failed';
        },
      });
  }

  selectRoute(routeId: number): void {
    if (this.sidebarPanelOnly && routeId !== this.selectedRouteId) {
      this.sidebarModePref = 'workbench';
      this.persistSidebarLayout();
    }
    this.selectedRouteId = routeId;
    this.dashboard.setRouteId(routeId);
    this.selectedRoute = this.routes.find((r) => r.id === routeId) || null;
    if (this.selectedRoute) {
      this.dashboard.setRouteMeta({
        origin_m: this.selectedRoute.origin_measure_m || 0,
        length_m: this.selectedRoute.length_m || 0,
      });
    }
    this.locateResult = null;
    this.boqAtChainage = null;
    this.qcReport = null;
    this.networkQcReport = null;
    this.markerSource.clear();
    this.segPreviewSource.clear();
    this.clashSource.clear();
    this.clearOffsetPreview();
    this.closeAssetDetail();
    if (this.selectedRoute && !this.demoWalkthroughPending) {
      this.segToKm = Math.min((this.selectedRoute.length_m || 1000) / 1000, 1);
      this.syncRouteEditFields();
    } else if (this.selectedRoute) {
      this.syncRouteEditFields();
    }
    this.loadRouteMap(routeId, () => {
      this.loadLifecycleData(routeId);
      if (this.demoWalkthroughPending) {
        this.demoWalkthroughPending = false;
        this.startDemoWalkthrough();
      }
    });
    if (this.activePanel === 'alignment') this.loadAlignment(routeId);
    if (this.activePanel === 'unm') this.loadUnmPanel();
    this.loadSectionLinks(routeId);
    this.loadSections();
    this.loadReportCatalog();
    this.reportPreview = null;
    this.loadLinkedDocuments(routeId);
    this.loadCalibrationPoints(routeId);
    if (this.pendingDocumentPan) {
      this.pendingDocumentPan = false;
      if (this.documentId) this.interpolateFromInput();
    }
  }

  private loadLifecycleData(routeId: number): void {
    this.lrs.listMeasurementRuns(routeId).subscribe({
      next: (res) => (this.measurementRuns = res.data || []),
      error: () => (this.measurementRuns = []),
    });
    this.lrs.listPavementConditions(routeId).subscribe({
      next: (res) => {
        this.pavementConditions = res.data || [];
        this.refreshPavementLayer();
      },
      error: () => (this.pavementConditions = []),
    });
    this.lrs.listMaintenanceEvents(routeId).subscribe({
      next: (res) => {
        this.maintenanceEvents = res.data || [];
        this.refreshMaintenanceLayer();
      },
      error: () => (this.maintenanceEvents = []),
    });
    this.lrs.listUtilityAssets(routeId).subscribe({
      next: (res) => {
        this.utilityAssets = res.data || [];
        this.refreshUtilityLayer();
        this.refreshNhaiCounts();
      },
      error: () => (this.utilityAssets = []),
    });
    this.lrs.listCarriageways(routeId).subscribe({
      next: (res) => {
        this.carriageways = res.data || [];
        this.refreshCarriagewayLayer();
      },
      error: () => (this.carriageways = []),
    });
    this.lrs.listRouteExtensions(routeId).subscribe({
      next: (res) => (this.routeExtensions = res.data || []),
      error: () => (this.routeExtensions = []),
    });
    this.lrs.listExcavationPermits(routeId).subscribe({
      next: (res) => (this.excavationPermits = res.data || []),
      error: () => (this.excavationPermits = []),
    });
    this.lrs.listRouteSnapshots(routeId).subscribe({
      next: (res) => (this.routeSnapshots = res.data || []),
      error: () => (this.routeSnapshots = []),
    });
    this.loadAlignment(routeId);
    this.clashReport = null;
    this.nearbyItems = [];
  }

  captureDeviceGps(): void {
    if (!('geolocation' in navigator)) {
      this.statusMessage = 'Geolocation not supported on this device';
      return;
    }
    this.gpsCapturing = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.gpsCapturing = false;
        this.gpsLng = Math.round(pos.coords.longitude * 1e6) / 1e6;
        this.gpsLat = Math.round(pos.coords.latitude * 1e6) / 1e6;
        this.statusMessage = `GPS captured ±${Math.round(pos.coords.accuracy)} m`;
        if (this.selectedRouteId) this.gpsToChainageFromCoords();
      },
      (err) => {
        this.gpsCapturing = false;
        this.statusMessage = `GPS capture failed: ${err.message}`;
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  }

  gpsToChainageFromCoords(): void {
    if (!this.selectedRouteId || this.gpsLng == null || this.gpsLat == null) return;
    this.lrs.gpsToChainage(this.selectedRouteId, this.gpsLng, this.gpsLat).subscribe({
      next: (res) => {
        this.locateResult = res.data;
        this.measureKm = res.data.measure_km;
        if (res.data.reference) this.referenceInput = res.data.reference;
        const pt = res.data.closest_point || res.data.point;
        if (pt?.coordinates) {
          const [lng, lat] = pt.coordinates as [number, number];
          this.showMarker(lng, lat, res.data.chainage_label);
        }
        this.statusMessage = res.data.chainage_label;
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'GPS lookup failed'),
    });
  }

  saveAttributeEvent(): void {
    if (!this.selectedRouteId) return;
    let data: Record<string, unknown> = {};
    try {
      data = JSON.parse(this.attributeDataJson || '{}');
    } catch {
      this.statusMessage = 'Invalid JSON in attribute data';
      return;
    }
    const toKm = this.measureToKm > this.measureKm ? this.measureToKm : this.measureKm + 0.1;
    this.lrs
      .createEvent(this.selectedRouteId, {
        measure_km: this.measureKm,
        measure_to_km: toKm,
        event_type: this.attributeEventType,
        label: this.attributeLabel || `${this.attributeEventType} ${this.measureKm.toFixed(2)}–${toKm.toFixed(2)} km`,
        properties: { ...data, layer: 'attribute' },
      })
      .subscribe({
        next: () => {
          this.statusMessage = 'Attribute event saved';
          this.loadRouteMap(this.selectedRouteId!);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Save failed'),
      });
  }

  createSurveyRun(): void {
    if (!this.selectedRouteId) return;
    this.lrs
      .createMeasurementRun(this.selectedRouteId, {
        run_date: new Date().toISOString().slice(0, 10),
        vehicle_id: 'NSV',
        start_measure_m: this.segFromKm * 1000,
        end_measure_m: this.segToKm * 1000,
      })
      .subscribe({
        next: (res) => {
          this.measurementRuns = [res.data, ...this.measurementRuns];
          this.selectedRunId = res.data.id;
          this.statusMessage = 'Measurement run created';
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Run failed'),
      });
  }

  savePavementSegment(): void {
    if (!this.selectedRouteId) return;
    const toKm = this.measureToKm > this.measureKm ? this.measureToKm : this.measureKm + 0.1;
    this.lrs
      .createPavementCondition(this.selectedRouteId, {
        run_id: this.selectedRunId,
        from_measure_km: this.measureKm,
        to_measure_km: toKm,
        pci: this.pciValue,
        iri: this.iriValue,
        surface_type: this.surfaceType,
      })
      .subscribe({
        next: () => {
          this.statusMessage = 'Pavement segment saved';
          this.loadLifecycleData(this.selectedRouteId!);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'PCI save failed'),
      });
  }

  saveMaintenance(): void {
    if (!this.selectedRouteId) return;
    const toKm = this.measureToKm > this.measureKm ? this.measureToKm : this.measureKm + 0.1;
    this.lrs
      .createMaintenanceEvent(this.selectedRouteId, {
        from_measure_km: this.measureKm,
        to_measure_km: toKm,
        work_type: this.maintWorkType,
        contractor: this.maintContractor,
        cost_inr: this.maintCostInr,
        warranty_months: this.maintWarrantyMonths,
        work_date: new Date().toISOString().slice(0, 10),
      })
      .subscribe({
        next: () => {
          this.statusMessage = 'Maintenance logged';
          this.loadLifecycleData(this.selectedRouteId!);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Maintenance save failed'),
      });
  }

  onUtilityDepthChange(): void {
    if (this.utilityDepthM != null && Math.abs(Number(this.utilityDepthM)) > 0 && this.utilityGrade === 'overground') {
      this.utilityGrade = 'underground';
    }
  }

  saveUtilityAsset(): void {
    if (!this.selectedRouteId) return;
    if (this.utilityDepthM != null && Math.abs(Number(this.utilityDepthM)) > 0 && this.utilityGrade === 'overground') {
      this.utilityGrade = 'underground';
    }
    const body: Record<string, unknown> = {
      utility_type: this.utilityType,
      measure_km: this.measureKm,
      label: this.utilityLabel || this.utilityType,
      depth_m: this.utilityDepthM,
      diameter_mm: this.utilityDiameterMm,
      lateral_offset_m: this.lateralOffsetM || 0,
      edge_offset_m: this.edgeOffsetM,
      side: this.assetSide || undefined,
      properties: { ...this.createSpecValues },
      grade: inferLrsGrade({
        grade: this.utilityGrade,
        depth_m: this.utilityDepthM,
      }),
    };
    if (this.utilityIsLinear && this.measureToKm > this.measureKm) {
      body['measure_to_km'] = this.measureToKm;
    }
    this.lrs.createUtilityAsset(this.selectedRouteId, body).subscribe({
      next: () => {
        this.statusMessage = 'Utility saved (UNM twin synced)';
        this.dashboard.markDirty();
        this.loadLifecycleData(this.selectedRouteId!);
        if (this.activePanel === 'unm') this.refreshUnmAssets();
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Utility save failed'),
    });
  }

  saveCarriageway(): void {
    if (!this.selectedRouteId) return;
    this.lrs
      .createCarriageway(this.selectedRouteId, {
        carriageway_code: this.carriagewayCode,
        lane_count: this.carriagewayLanes,
        direction: this.carriagewayDirection,
        carriageway_side: this.carriagewaySide,
        lateral_offset_m: this.carriagewayOffsetM,
        from_measure_km: this.measureKm,
        to_measure_km: this.measureToKm > this.measureKm ? this.measureToKm : undefined,
      })
      .subscribe({
        next: () => {
          this.statusMessage = 'Carriageway saved';
          this.loadLifecycleData(this.selectedRouteId!);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Carriageway save failed'),
      });
  }

  loadEventTypeCatalog(): void {
    this.lrs.listEventTypes().subscribe({
      next: (res) => {
        this.catalogEventTypes = res.data || [];
        if (this.catalogEventTypes.length) {
          this.assetTypes = this.catalogEventTypes.map((t) => {
            const fallback = getLrsAssetTypeMeta(t.id);
            return {
              id: t.id,
              label: t.label,
              color: t.color || fallback?.color || '#64748b',
              icon: fallback?.icon || '•',
              iconPath: fallback?.iconPath || 'assets/icons/lrs/sign.svg',
              symbolCode: t.symbol_code || t.id,
              geometryKind: t.geometry_kind,
              clusterable: t.geometry_kind === 'point',
            } as LrsAssetTypeMeta;
          });
        }
      },
      error: () => {
        /* keep LRS_ASSET_EVENT_TYPES fallback */
      },
    });
  }

  loadSections(): void {
    if (!this.selectedRouteId) {
      this.sections = [];
      this.routeSegments = [];
      return;
    }
    this.lrs.listSections(this.selectedRouteId).subscribe({
      next: (res) => (this.sections = res.data || []),
      error: () => (this.sections = []),
    });
    this.lrs.listRouteSegments(this.selectedRouteId).subscribe({
      next: (res) => (this.routeSegments = res.data || []),
      error: () => (this.routeSegments = []),
    });
  }

  createSection(): void {
    if (!this.selectedRouteId) return;
    const code = this.newSectionCode.trim();
    if (!code) {
      this.statusMessage = 'Enter a section code';
      return;
    }
    this.lrs
      .createSection(this.selectedRouteId, {
        section_code: code,
        section_name: this.newSectionName.trim() || undefined,
        from_measure_km: this.newSectionFromKm,
        to_measure_km: this.newSectionToKm,
        surface_type: this.newSectionSurface.trim() || undefined,
        lane_width_m: this.newSectionLaneWidth ?? undefined,
      })
      .subscribe({
        next: (res) => {
          this.statusMessage = `Section ${res.data.section_code} created`;
          this.newSectionCode = '';
          this.newSectionName = '';
          this.selectedSectionId = res.data.id;
          this.loadSections();
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Create section failed'),
      });
  }

  assignSegmentMeta(): void {
    if (!this.selectedSegmentId) return;
    this.lrs
      .updateRouteSegment(this.selectedSegmentId, {
        section_id: this.assignSectionId,
        parent_segment_id: this.assignParentSegmentId,
        carriageway_side: (this.assignCarriagewaySide || null) as LrsRouteSegment['carriageway_side'],
      })
      .subscribe({
        next: () => {
          this.statusMessage = 'Segment metadata updated';
          this.loadSections();
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Update segment failed'),
      });
  }

  loadCorridorInventory(): void {
    if (!this.selectedRouteId) return;
    const opts: { side?: string; abs_offset_min_m?: number; abs_offset_max_m?: number } = {};
    if (this.corridorFilterSide) opts.side = this.corridorFilterSide;
    if (this.corridorOffsetFromM != null) opts.abs_offset_min_m = this.corridorOffsetFromM;
    if (this.corridorOffsetToM != null) opts.abs_offset_max_m = this.corridorOffsetToM;
    this.lrs.corridorInventory(this.selectedRouteId, this.segFromKm, this.segToKm, opts).subscribe({
      next: (res) => {
        this.lastCorridor = res.data;
        this.corridorSummary = res.data.summary;
        this.corridorLayerQa = res.data.layer_qa || null;
        this.statusMessage = `Corridor ${this.segFromKm}–${this.segToKm} km loaded`;
        this.applyCorridorToMap(res.data);
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Corridor load failed'),
    });
  }

  exportCorridorGeoJson(): void {
    if (!this.lastCorridor) {
      this.loadCorridorInventory();
      return;
    }
    const blob = new Blob([JSON.stringify(this.lastCorridor, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `corridor-${this.selectedRoute?.route_code || 'route'}-${this.segFromKm}-${this.segToKm}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private applyCorridorToMap(data: LrsCorridorInventory): void {
    this.clearHighwayEventSources();
    const hw = this.geoJson.readFeatures(data.highway_assets, { featureProjection: 'EPSG:3857' });
    for (const f of hw) {
      this.addHighwayEventFeature(f);
    }
    const attr = this.geoJson.readFeatures(data.attribute_events, { featureProjection: 'EPSG:3857' });
    this.linearEventSource.addFeatures(attr);
    this.refreshPavementFromList(data.pavement_conditions);
    this.refreshMaintenanceFromList(data.maintenance_events);
    this.refreshUtilityFromList(data.utility_assets);
    if (data.route_segment?.geometry) {
      this.segPreviewSource.clear();
      const seg = this.geoJson.readFeatures(data.route_segment, { featureProjection: 'EPSG:3857' });
      this.segPreviewSource.addFeatures(seg);
      this.fitToSource(this.segPreviewSource);
    }
  }

  private refreshPavementLayer(): void {
    this.refreshPavementFromList(this.pavementConditions);
  }

  private refreshPavementFromList(list: LrsPavementCondition[]): void {
    this.pavementSource.clear();
    for (const p of list) {
      if (!p.geometry) continue;
      const feats = this.geoJson.readFeatures(
        { type: 'Feature', geometry: p.geometry, properties: { pci: p.pci } },
        { featureProjection: 'EPSG:3857' }
      );
      this.pavementSource.addFeatures(feats);
    }
  }

  private refreshMaintenanceLayer(): void {
    this.refreshMaintenanceFromList(this.maintenanceEvents);
  }

  private refreshMaintenanceFromList(list: LrsMaintenanceEvent[]): void {
    this.maintenanceSource.clear();
    for (const m of list) {
      if (!m.geometry) continue;
      const feats = this.geoJson.readFeatures(
        { type: 'Feature', geometry: m.geometry, properties: { work_type: m.work_type } },
        { featureProjection: 'EPSG:3857' }
      );
      this.maintenanceSource.addFeatures(feats);
    }
  }

  private refreshUtilityLayer(): void {
    this.refreshUtilityFromList(this.utilityAssets);
  }

  private refreshUtilityFromList(list: LrsUtilityAsset[]): void {
    this.utilityLineSource.clear();
    this.utilityPointRawSource.clear();
    for (const u of list) {
      if (!u.geometry) continue;
      const feats = this.geoJson.readFeatures(
        {
          type: 'Feature',
          geometry: u.geometry,
          properties: { utility_type: u.utility_type, label: u.label },
        },
        { featureProjection: 'EPSG:3857' }
      );
      for (const f of feats) {
        applySymbolProperties(f, {
          id: u.id,
          utility_type: u.utility_type,
          label: u.label,
          measure_m: u.measure_m,
          measure_km: u.measure_m / 1000,
          depth_m: u.depth_m,
          grade: u.grade || inferLrsGrade(u),
          lifecycle_status: u.lifecycle_status,
          condition: u.condition,
          map_status: u.map_status,
          lateral_offset_m: u.lateral_offset_m,
          edge_offset_m: u.edge_offset_m,
          side: u.side,
          diameter_mm: u.diameter_mm,
          asset_kind: 'utility',
        });
        if (!this.isCatalogVisible(f)) continue;
        if (f.getGeometry() instanceof LineString) {
          this.utilityLineSource.addFeature(f);
        } else {
          this.utilityPointRawSource.addFeature(f);
        }
      }
    }
  }

  private refreshCarriagewayLayer(): void {
    this.carriagewaySource.clear();
    for (const c of this.carriageways) {
      if (!c.geometry) continue;
      const feats = this.geoJson.readFeatures(
        { type: 'Feature', geometry: c.geometry, properties: { code: c.carriageway_code } },
        { featureProjection: 'EPSG:3857' }
      );
      this.carriagewaySource.addFeatures(feats);
    }
  }

  /** After road geometry is painted, run asset/drawing overlay work. */
  private afterRoadGeometryReady(cb: () => void): void {
    const run = () => {
      this.map?.renderSync();
      cb();
      this.map?.render();
    };
    if (!this.map) {
      setTimeout(run, 50);
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(run));
  }

  private loadRouteMap(routeId: number, onOverlaysReady?: () => void): void {
    this.loading = true;
    this.statusMessage = 'Loading road geometry…';
    this.lrs.getRouteMap(routeId, this.historyAtTime ? { at_time: new Date(this.historyAtTime).toISOString() } : undefined).subscribe({
      next: (res) => {
        const routeFeature = res.data?.route;
        const eventsFc = res.data?.events;
        this.routeSource.clear();
        this.clearHighwayEventSources();
        this.chainageSource.clear();
        if (routeFeature) {
          const feats = this.geoJson.readFeatures(routeFeature, { featureProjection: 'EPSG:3857' });
          feats.forEach((f) => {
            const code = String(f.get('route_code') || this.selectedRoute?.route_code || '');
            const name = String(f.get('route_name') || this.selectedRoute?.route_name || '');
            if (!f.get('label')) f.set('label', [code, name].filter(Boolean).join(' · '));
          });
          this.routeSource.addFeatures(feats);
          this.fitToSource(this.routeSource);
          this.statusMessage = 'Road geometry loaded — loading assets…';
        } else {
          this.statusMessage = 'No route geometry — loading assets…';
        }
        this.loading = false;

        this.afterRoadGeometryReady(() => {
          if (eventsFc) {
            const evFeats = this.geoJson.readFeatures(eventsFc, { featureProjection: 'EPSG:3857' });
            for (const f of evFeats) {
              const t = f.get('event_type');
              if (t === 'chainage') {
                this.chainageSource.addFeature(f);
                continue;
              }
              this.addHighwayEventFeature(f);
            }
          }
          this.refreshEventsList(routeId);
          preloadLrsIcons().then(() => {
            this.refreshMapSymbolLayers();
            onOverlaysReady?.();
          });
        });
      },
      error: (e) => {
        this.loading = false;
        this.demoWalkthroughPending = false;
        this.mapError = e?.error?.error || 'Failed to load route geometry';
      },
    });
  }

  loadCalibrationPoints(routeId: number): void {
    this.lrs.listCalibrationPoints(routeId).subscribe({
      next: (res) => {
        this.calibrationPoints = res.data || [];
        this.calibSource.clear();
        for (const c of this.calibrationPoints) {
          if (c.geometry) {
            const feats = this.geoJson.readFeatures(
              { type: 'Feature', geometry: c.geometry, properties: {} },
              { featureProjection: 'EPSG:3857' }
            );
            feats.forEach((f) => {
              f.set('label', c.label || c.chainage_label);
              this.calibSource.addFeature(f);
            });
          }
        }
      },
      error: () => (this.calibrationPoints = []),
    });
  }

  addCalibrationAtChainage(): void {
    if (!this.selectedRouteId) return;
    this.lrs
      .createCalibrationPoint(this.selectedRouteId, {
        measure_km: this.measureKm,
        point_type: 'km_stone',
        label: `CH @ ${this.measureKm.toFixed(3)} km`,
      })
      .subscribe({
        next: () => {
          this.statusMessage = 'Calibration point saved';
          this.loadCalibrationPoints(this.selectedRouteId!);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Calibration failed'),
      });
  }

  confirmMapMatch(): void {
    if (!this.selectedRouteId) return;
    this.lrs.updateRoute(this.selectedRouteId, { map_ok: true }).subscribe({
      next: (res) => {
        if (this.selectedRoute) {
          this.selectedRoute = { ...this.selectedRoute, ...res.data, map_ok: true, map_issues: [] };
        }
        this.statusMessage = 'Map gate accepted — OSM / basemap matches this corridor';
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Confirm map failed'),
    });
  }

  runRouteQc(): void {
    if (!this.selectedRouteId) return;
    this.lrs.validateRouteQc(this.selectedRouteId).subscribe({
      next: (res) => {
        this.qcReport = res.data;
        this.calibrationReport = res.data.calibration_accuracy ?? null;
        if (res.data.network) this.networkQcReport = res.data.network;
        const cal = this.calibrationReport;
        const calNote =
          cal && cal.anchor_count > 0
            ? ` · calibration ${cal.accuracy_pct}%`
            : '';
        this.statusMessage = res.data.ok
          ? `LRS QC passed${calNote}`
          : `${res.data.issue_count} QC issue(s)${calNote}`;
        if (this.selectedRoute) {
          const networkErrors = (res.data.network?.issues || []).filter((i) => i.severity === 'error');
          const lrsErrors = (res.data.issues || []).filter(
            (i) => i.severity === 'error' && !String(i.code || '').startsWith('NETWORK_')
          );
          this.selectedRoute = {
            ...this.selectedRoute,
            road_ok: networkErrors.length === 0,
            lrs_ok: lrsErrors.length === 0,
            lrs_issues: res.data.issues,
            road_issues: res.data.network?.issues,
          };
        }
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'QC failed'),
    });
  }

  runNetworkQc(): void {
    this.lrs
      .validateNetworkQc({
        org_id: this.selectedRoute?.org_id,
        project_id: this.projectId || undefined,
        snap_m: 5,
      })
      .subscribe({
        next: (res) => {
          this.networkQcReport = res.data;
          this.statusMessage = res.data.ok
            ? `Network QC OK (${res.data.route_count} routes)`
            : `${res.data.issue_count} network issue(s) · ${res.data.repairs?.length || 0} repair(s)`;
          this.applyNetworkQcOverlay(res.data);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Network QC failed'),
      });
  }

  private applyNetworkQcOverlay(report: LrsNetworkQcReport): void {
    this.segPreviewSource.clear();
    const fc = report.repair_points;
    if (!fc?.features?.length) return;
    const feats = this.geoJson.readFeatures(fc, { featureProjection: 'EPSG:3857' });
    this.segPreviewSource.addFeatures(feats);
  }

  runSurveyDeviation(): void {
    if (!this.selectedRouteId || !this.surveyObservationsCsv.trim()) {
      this.statusMessage = 'Paste observations CSV: lng,lat,field_measure_km[,label]';
      return;
    }
    const lines = this.surveyObservationsCsv.trim().split(/\r?\n/).filter(Boolean);
    const start = /[a-z]/i.test(lines[0].split(',')[0]) ? 1 : 0; // skip header row
    const observations = lines.slice(start).map((line, i) => {
      const [lng, lat, km, label] = line.split(',').map((c) => c.trim());
      return {
        lng: parseFloat(lng),
        lat: parseFloat(lat),
        field_measure_km: parseFloat(km),
        label: label || `OBS-${i + 1}`,
      };
    });
    this.lrs.surveyDeviationReport(this.selectedRouteId, { observations }).subscribe({
      next: (res) => {
        this.surveyReport = res.data;
        this.statusMessage = res.data.ok
          ? `Survey deviation OK — ${res.data.pass_pct}% within tolerance`
          : `Survey deviation: ${res.data.within_tolerance_count}/${res.data.valid_count} within tolerance`;
        this.persistSurveyDeviation(res.data);
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Survey comparison failed'),
    });
  }

  private persistSurveyDeviation(report: any): void {
    const projectId = this.projectId || this.selectedRoute?.project_id;
    if (!projectId) return;
    this.api.listProjectSurveyStages({ project_id: projectId, limit: 1 }).subscribe({
      next: (res: { data?: Array<{ id?: number; remarks?: unknown }> }) => {
        const row = res?.data?.[0];
        if (!row?.id) return;
        const remarks = this.parseSurveyRemarks(row.remarks);
        remarks['lrs_deviation'] = {
          report,
          csv: this.surveyObservationsCsv,
          route_id: this.selectedRouteId,
          at: new Date().toISOString(),
        };
        this.api.updateProjectSurveyStage(Number(row.id), { remarks }).subscribe({
          next: () => {
            this.statusMessage = `${this.statusMessage} · saved to survey`;
          },
          error: () => undefined,
        });
      },
      error: () => undefined,
    });
  }

  private parseSurveyRemarks(raw: unknown): Record<string, unknown> {
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) return { ...(raw as Record<string, unknown>) };
    try {
      const parsed = JSON.parse(String(raw));
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return { legacy_remarks: raw };
    }
  }

  runAcceptanceEvidence(): void {
    if (!this.selectedRouteId) return;
    this.lrs.acceptanceEvidenceReport(this.selectedRouteId).subscribe({
      next: (res) => {
        this.acceptanceReport = res.data;
        if (res.data.survey) this.surveyReport = res.data.survey;
        this.statusMessage = res.data.ok
          ? 'Acceptance pack passed (TC-03 / 05 / 12 / 13)'
          : 'Acceptance pack has failing checks — review Survey panel';
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Acceptance evidence failed'),
    });
  }

  exportSurveyDeviation(format: 'csv' | 'json' | 'pdf' = 'csv'): void {
    if (!this.surveyReport) {
      this.statusMessage = 'Run a survey comparison first';
      return;
    }
    const report: LrsExportReport = {
      meta: {
        report_type: 'survey-deviation',
        title: 'Survey deviation (field vs LRS)',
        route_id: this.surveyReport.route_id,
        route_code: this.surveyReport.route_code,
        route_name: this.selectedRoute?.route_name || '',
        length_km: (this.selectedRoute?.length_m || 0) / 1000,
        from_km: null,
        to_km: null,
        generated_at: new Date().toISOString(),
        standard: 'TC-LRS-13',
        ok: this.surveyReport.ok,
      },
      columns: [
        { key: 'label', label: 'Label' },
        { key: 'field_measure_km', label: 'Field km' },
        { key: 'lrs_measure_km', label: 'LRS km' },
        { key: 'deviation_m', label: 'Δ m' },
        { key: 'error_m_per_km', label: 'm/km' },
        { key: 'lateral_offset_m', label: 'Lateral m' },
        { key: 'within_tolerance', label: 'OK' },
      ],
      rows: this.surveyReport.observations.map((o) => ({
        label: o.label,
        field_measure_km: o.field_measure_km,
        lrs_measure_km: o.lrs_measure_km,
        deviation_m: o.deviation_m,
        error_m_per_km: o.error_m_per_km,
        lateral_offset_m: o.lateral_offset_m,
        within_tolerance: o.within_tolerance ? 'Yes' : 'No',
      })),
      summary: {
        ok: this.surveyReport.ok,
        pass_pct: this.surveyReport.pass_pct,
        max_abs_deviation_m: this.surveyReport.max_abs_deviation_m,
      },
    };
    if (format === 'pdf') {
      this.downloadReportPdf(report);
      this.statusMessage = 'Survey deviation PDF exported';
      return;
    }
    if (format === 'json') {
      const blob = new Blob([JSON.stringify(this.surveyReport, null, 2)], { type: 'application/json' });
      this.triggerBlobDownload(blob, `survey-deviation-${this.surveyReport.route_code}.json`);
      return;
    }
    const header = report.columns.map((c) => c.label).join(',');
    const body = report.rows
      .map((row) => report.columns.map((c) => String(row[c.key] ?? '')).join(','))
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
    this.triggerBlobDownload(blob, `survey-deviation-${this.surveyReport.route_code}.csv`);
  }

  runCalibrationAccuracy(): void {
    if (!this.selectedRouteId) return;
    this.lrs.calibrationAccuracyReport(this.selectedRouteId).subscribe({
      next: (res) => {
        this.calibrationReport = res.data;
        this.statusMessage = res.data.ok
          ? `Calibration accuracy ${res.data.accuracy_pct}% (≥${res.data.target_pct}%)`
          : `Calibration accuracy ${res.data.accuracy_pct ?? 0}% — review anchors`;
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Calibration report failed'),
    });
  }

  loadReportCatalog(): void {
    if (!this.selectedRouteId) return;
    this.lrs.listLrsReports(this.selectedRouteId).subscribe({
      next: (res) => (this.reportCatalog = res.data || []),
      error: () => (this.reportCatalog = []),
    });
  }

  previewLrsReport(): void {
    if (!this.selectedRouteId) return;
    const opts = this.reportNeedsRange()
      ? { fromKm: this.segFromKm, toKm: this.segToKm }
      : undefined;
    this.lrs.getLrsReportJson(this.selectedRouteId, this.reportType, opts).subscribe({
      next: (res) => {
        this.reportPreview = res.data;
        this.statusMessage = `${res.data.meta.title}: ${res.data.rows.length} rows`;
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Report preview failed'),
    });
  }

  exportLrsReport(): void {
    if (!this.selectedRouteId) return;
    const opts = this.reportNeedsRange()
      ? { fromKm: this.segFromKm, toKm: this.segToKm }
      : undefined;

    if (this.reportFormat === 'pdf') {
      this.lrs.getLrsReportJson(this.selectedRouteId, this.reportType, opts).subscribe({
        next: (res) => {
          this.reportPreview = res.data;
          this.downloadReportPdf(res.data);
          this.statusMessage = `PDF exported: ${res.data.meta.title}`;
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'PDF export failed'),
      });
      return;
    }

    if (this.reportFormat === 'json') {
      this.lrs.getLrsReportJson(this.selectedRouteId, this.reportType, opts).subscribe({
        next: (res) => {
          this.reportPreview = res.data;
          const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
          this.triggerBlobDownload(blob, `${res.data.meta.report_type}-${res.data.meta.route_code}.json`);
          this.statusMessage = `JSON exported: ${res.data.meta.title}`;
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'JSON export failed'),
      });
      return;
    }

    this.lrs.downloadLrsReport(this.selectedRouteId, this.reportType, this.reportFormat, opts).subscribe({
      next: (blob) => {
        const ext = this.reportFormat === 'xlsx' ? 'xlsx' : 'csv';
        const code = this.selectedRoute?.route_code || 'route';
        this.triggerBlobDownload(blob, `${this.reportType}-${code}.${ext}`);
        this.statusMessage = `${this.reportFormat.toUpperCase()} exported`;
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Report export failed'),
    });
  }

  reportNeedsRange(): boolean {
    const entry = this.reportCatalog.find((r) => r.id === this.reportType);
    return entry?.supports_range ?? ['sld', 'asset-inventory', 'utility-inventory', 'corridor-inventory'].includes(this.reportType);
  }

  private triggerBlobDownload(blob: Blob, filename: string): void {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private downloadReportPdf(report: LrsExportReport): void {
    const doc = new jsPDF({ orientation: report.rows.length > 20 ? 'landscape' : 'portrait' });
    const m = report.meta;
    doc.setFontSize(14);
    doc.text(m.title, 14, 16);
    doc.setFontSize(10);
    doc.text(`Route: ${m.route_code} — ${m.route_name}`, 14, 24);
    doc.text(`Generated: ${new Date(m.generated_at).toLocaleString()}`, 14, 30);
    if (m.from_km != null && m.to_km != null) {
      doc.text(`Chainage: ${m.from_km} – ${m.to_km} km`, 14, 36);
    }
    if (m.accuracy_pct != null) {
      doc.text(`Calibration accuracy: ${m.accuracy_pct}%`, 14, 42);
    }
    const head = [report.columns.map((c) => c.label)];
    const body = report.rows.map((row) =>
      report.columns.map((c) => String(row[c.key] ?? ''))
    );
    autoTable(doc, {
      head,
      body,
      startY: m.from_km != null || m.accuracy_pct != null ? 48 : 38,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 98, 145] },
    });
    doc.save(`${m.report_type}-${m.route_code}.pdf`);
  }

  bulkImportAssetsFromCsv(): void {
    if (!this.selectedRouteId || !this.bulkImportCsv.trim()) {
      this.statusMessage = 'Paste CSV with header: measure_km,event_type,label,side,offset_m';
      return;
    }
    this.lrs.bulkImportAssets(this.selectedRouteId, { csv: this.bulkImportCsv }).subscribe({
      next: (res) => {
        const d: LrsBulkImportResult = res.data;
        this.statusMessage = `Imported ${d.created}/${d.total} assets${d.failed ? ` (${d.failed} failed)` : ''}`;
        this.bulkImportCsv = '';
        this.loadRouteMap(this.selectedRouteId!);
        this.refreshEventsList(this.selectedRouteId!);
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Bulk import failed'),
    });
  }

  previewDynamicSegment(): void {
    if (!this.selectedRouteId || this.segToKm <= this.segFromKm) {
      this.statusMessage = 'Set valid from/to km for segment preview';
      return;
    }
    this.lrs.dynamicSegment(this.selectedRouteId, this.segFromKm, this.segToKm).subscribe({
      next: (res) => {
        this.segPreviewSource.clear();
        const data = res.data;
        if (data.route?.geometry) {
          const segFeats = this.geoJson.readFeatures(data.route, { featureProjection: 'EPSG:3857' });
          this.segPreviewSource.addFeatures(segFeats);
        }
        if (data.events?.features) {
          const evFeats = this.geoJson.readFeatures(data.events, { featureProjection: 'EPSG:3857' });
          for (const f of evFeats) {
            this.addHighwayEventFeature(f.clone());
          }
        }
        this.statusMessage = `Segment ${this.segFromKm}–${this.segToKm} km (${(data.segment_length_m / 1000).toFixed(2)} km)`;
        if (this.segPreviewSource.getFeatures().length) {
          this.fitToSource(this.segPreviewSource);
        }
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Dynamic segment failed'),
    });
  }

  clearSegmentPreview(): void {
    this.segPreviewSource.clear();
    if (this.selectedRouteId) this.loadRouteMap(this.selectedRouteId);
  }

  applyAssetFilter(): void {
    if (!this.selectedRouteId) return;
    this.refreshEventsList(this.selectedRouteId);
    this.rebuildEventLayersFromList();
  }

  onHistoryTimeChange(): void {
    if (!this.selectedRouteId) return;
    this.loadRouteMap(this.selectedRouteId);
  }

  private rebuildEventLayersFromList(): void {
    this.clearHighwayEventSources();
    for (const e of this.assetEvents) {
      if (!e.geometry) continue;
      const feat = this.geoJson.readFeature(
        { type: 'Feature', geometry: e.geometry, properties: { ...e, event_type: e.event_type } },
        { featureProjection: 'EPSG:3857' }
      ) as Feature;
      this.addHighwayEventFeature(feat);
    }
  }

  private loadSectionLinks(routeId: number): void {
    this.lrs.listSectionLinks(routeId).subscribe({
      next: (res) => (this.sectionLinks = res.data || []),
      error: () => (this.sectionLinks = []),
    });
  }

  private loadLinkedDocuments(routeId: number): void {
    this.lrs.listDocumentsByLrs({ route_id: routeId }).subscribe({
      next: (res) => (this.linkedDocuments = res.data || []),
      error: () => (this.linkedDocuments = []),
    });
  }

  private refreshEventsList(routeId: number): void {
    const opts: {
      event_type?: string;
      fromKm?: number;
      toKm?: number;
      at_time?: string;
      side?: string;
      abs_offset_min_m?: number;
      abs_offset_max_m?: number;
    } = {};
    if (this.assetFilterType) opts.event_type = this.assetFilterType;
    if (this.assetFilterFromKm != null) opts.fromKm = this.assetFilterFromKm;
    if (this.assetFilterToKm != null) opts.toKm = this.assetFilterToKm;
    if (this.historyAtTime) opts.at_time = new Date(this.historyAtTime).toISOString();
    if (this.assetFilterSide) opts.side = this.assetFilterSide;
    if (this.assetFilterOffsetFromM != null) opts.abs_offset_min_m = this.assetFilterOffsetFromM;
    if (this.assetFilterOffsetToM != null) opts.abs_offset_max_m = this.assetFilterOffsetToM;
    this.lrs.listEvents(routeId, Object.keys(opts).length ? opts : undefined).subscribe({
      next: (res) => {
        const feats = res.data?.features || [];
        const counts: Record<string, number> = {};
        this.assetEvents = feats
          .filter((f) => {
            const et = String(f.properties?.['event_type'] || '');
            return et !== 'chainage' && (this.assetFilterType ? et === this.assetFilterType : isLrsAssetEventType(et) || et === 'asset');
          })
          .map((f) => {
            const et = String(f.properties?.['event_type'] || '');
            counts[et] = (counts[et] || 0) + 1;
            const geom = f.geometry as GeoJSON.Point | GeoJSON.LineString | undefined;
            const rawProps = (f.properties || {}) as Record<string, unknown>;
            const nestedProps =
              rawProps['properties'] && typeof rawProps['properties'] === 'object'
                ? (rawProps['properties'] as Record<string, unknown>)
                : {};
            return {
              id: Number(f.properties?.['id'] || f.id),
              route_id: routeId,
              measure_m: Number(f.properties?.['measure_m'] || 0),
              measure_to_m: f.properties?.['measure_to_m'] as number | undefined,
              event_type: et,
              label: String(f.properties?.['label'] || ''),
              chainage_label: String(f.properties?.['chainage_label'] || ''),
              chainage_to_label: String(f.properties?.['chainage_to_label'] || ''),
              is_linear: !!f.properties?.['is_linear'],
              side: String(f.properties?.['side'] || ''),
              offset_m: f.properties?.['offset_m'] != null ? Number(f.properties['offset_m']) : undefined,
              edge_offset_m: f.properties?.['edge_offset_m'] != null ? Number(f.properties['edge_offset_m']) : undefined,
              geometry: geom,
              properties: {
                ...nestedProps,
                ...rawProps,
                asset_ref: String(rawProps['asset_ref'] || nestedProps['asset_ref'] || ''),
              },
            };
          });
        this.assetCounts = counts;
        this.refreshNhaiCounts();
      },
    });
  }

  changeBaseMap(key: BaseMapKey): void {
    this.baseMapKey = key;
    if (!this.map || !this.baseLayer) return;
    this.map.removeLayer(this.baseLayer);
    this.baseLayer = this.baseLayers[key];
    this.map.getLayers().insertAt(0, this.baseLayer);
  }

  onGeoserverToggle(enabled?: boolean): void {
    const on = enabled ?? this.showGeoserverWms;
    if (!this.map) return;
    const wmsUrl = environment.geoserverWmsUrl;
    if (on && !wmsUrl) {
      this.statusMessage = 'Set geoserverWmsUrl in environment.ts';
      this.showGeoserverWms = false;
      return;
    }
    if (this.geoserverLayerRef) {
      this.map.removeLayer(this.geoserverLayerRef);
      this.geoserverLayerRef = undefined;
    }
    if (on && wmsUrl) {
      this.geoserverLayerRef = new ImageLayer({
        source: new ImageWMS({
          url: wmsUrl,
          params: { LAYERS: this.geoserverLayer, TILED: true, FORMAT: 'image/png' },
          ratio: 1,
          serverType: 'geoserver',
        }),
        opacity: 0.55,
        zIndex: 2,
      });
      this.map.addLayer(this.geoserverLayerRef);
    }
  }

  loadWfsEvents(): void {
    const wfsUrl = environment.geoserverWfsUrl;
    if (!wfsUrl || !this.map) {
      this.statusMessage = 'Set geoserverWfsUrl in environment.ts';
      return;
    }
    const extent = this.map.getView().calculateExtent(this.map.getSize() || [800, 600]);
    const sep = wfsUrl.includes('?') ? '&' : '?';
    const url = `${wfsUrl}${sep}SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&TYPENAME=lrs:events_wms&BBOX=${extent.join(',')}&SRSNAME=EPSG:3857`;
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        const features = new WFS({ version: '1.1.0' }).readFeatures(text, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        });
        for (const f of features) {
          this.addHighwayEventFeature(f as Feature);
        }
        this.statusMessage = `WFS: ${features.length} features`;
      })
      .catch(() => (this.statusMessage = 'WFS load failed'));
  }

  importOsmFromMapExtent(): void {
    if (!this.hasNetwork) {
      this.statusMessage = 'Create a road network first. Routes stay locked until then.';
      return;
    }
    if (!this.map) return;
    const extent = this.map.getView().calculateExtent(this.map.getSize() || [800, 600]);
    const sw = toLonLat([extent[0], extent[1]]);
    const ne = toLonLat([extent[2], extent[3]]);
    this.loading = true;
    this.lrs
      .importFromOsm({
        bbox: [sw[1], sw[0], ne[1], ne[0]],
        route_ref: this.osmRouteRef || undefined,
        route_code: this.newRouteCode || undefined,
        route_name: this.newRouteName || undefined,
        project_id: this.projectId || undefined,
        network_id: this.selectedNetworkId || undefined,
        subnetwork_id: this.selectedSubnetworkId || undefined,
      })
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.statusMessage = `Imported ${res.data.route_code}` +
            (res.data.length_m ? ` · ${(res.data.length_m / 1000).toFixed(2)} km from geometry` : '');
          this.loadRoutes(this.projectId || undefined);
          this.selectRoute(res.data.id);
        },
        error: (e) => {
          this.loading = false;
          this.statusMessage = e?.error?.error || 'OSM import failed';
        },
      });
  }

  importRoadProject(): void {
    if (!this.hasNetwork) {
      this.statusMessage = 'Create a road network first. Routes stay locked until then.';
      return;
    }
    const pid = this.roadProjectImportId.trim();
    if (!pid) return;
    this.loading = true;
    this.lrs.importFromRoadProject(pid, {
      route_code: this.newRouteCode || pid,
      network_id: this.selectedNetworkId || undefined,
      subnetwork_id: this.selectedSubnetworkId || undefined,
    }).subscribe({
      next: (res) => {
        this.loading = false;
        this.statusMessage = `Linked ${res.data.sections_linked} sections`;
        this.loadRoutes(this.projectId || undefined);
        this.selectRoute(res.data.route.id);
      },
      error: (e) => {
        this.loading = false;
        this.statusMessage = e?.error?.error || 'Import failed';
      },
    });
  }

  onShapefileSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!this.hasNetwork) {
      this.statusMessage = 'Create a road network first. Routes stay locked until then.';
      return;
    }
    this.shapefileImporting = true;
    this.lrs
      .importShapefile(file, {
        route_id: this.selectedRouteId || undefined,
        route_code: this.newRouteCode || undefined,
        route_name: this.newRouteName || undefined,
        project_id: this.projectId || undefined,
        network_id: this.selectedNetworkId || undefined,
        subnetwork_id: this.selectedSubnetworkId || undefined,
      })
      .subscribe({
        next: (res) => {
          this.shapefileImporting = false;
          const d = res.data;
          this.statusMessage = `Shapefile: ${d.routes} route(s), ${d.events} event(s)` +
            (d.errors?.length ? ` · ${d.errors.length} skipped` : '');
          this.loadRoutes(this.projectId || undefined);
          if (d.route_id) this.selectRoute(d.route_id);
        },
        error: (e) => {
          this.shapefileImporting = false;
          this.statusMessage = e?.error?.error || 'Shapefile import failed';
        },
      });
  }

  exportShapefile(): void {
    if (!this.selectedRouteId) return;
    this.lrs
      .downloadShapefile(this.selectedRouteId, this.shapefileLayer, {
        fromKm: this.segFromKm,
        toKm: this.segToKm,
      })
      .subscribe({
        next: (blob) => {
          const code = this.selectedRoute?.route_code || 'lrs';
          this.triggerBlobDownload(blob, `${code}-${this.shapefileLayer}.zip`);
          this.statusMessage = `Shapefile exported (${this.shapefileLayer})`;
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Shapefile export failed'),
      });
  }

  exportClash3dGeoJson(): void {
    if (!this.selectedRouteId) return;
    this.lrs
      .detectClashes(this.selectedRouteId, this.segFromKm, this.segToKm, {
        max_depth_m: this.clashMaxDepthM,
        min_vertical_sep_m: this.clashMinVerticalSepM,
        min_3d_sep_m: this.clashMin3dSepM,
        format: 'geojson',
      })
      .subscribe({
        next: (res) => {
          const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/geo+json' });
          const code = this.selectedRoute?.route_code || 'lrs';
          this.triggerBlobDownload(blob, `${code}-clash-3d.geojson`);
          this.statusMessage = '3D clash GeoJSON exported';
        },
        error: (e) => (this.statusMessage = e?.error?.error || '3D clash export failed'),
      });
  }

  captureGeometrySnapshot(): void {
    if (!this.selectedRouteId) return;
    this.lrs.captureRouteSnapshot(this.selectedRouteId, 'manual').subscribe({
      next: (res) => {
        this.statusMessage = `Snapshot #${res.data.id} captured`;
        this.lrs.listRouteSnapshots(this.selectedRouteId!).subscribe({
          next: (s) => (this.routeSnapshots = s.data || []),
        });
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Snapshot failed'),
    });
  }

  viewSnapshot(snap: LrsRouteSnapshot): void {
    this.historyAtTime = this.toDatetimeLocal(snap.captured_at);
    if (this.selectedRouteId) this.loadRouteMap(this.selectedRouteId);
    this.statusMessage = `As-of ${snap.reason} snapshot ${new Date(snap.captured_at).toLocaleString()}`;
  }

  private toDatetimeLocal(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  generateChainageMarkers(): void {
    if (!this.selectedRouteId) return;
    this.lrs.generateChainageMarkers(this.selectedRouteId, this.chainageIntervalKm, true).subscribe({
      next: () => {
        this.statusMessage = 'Chainage markers saved as events';
        this.loadRouteMap(this.selectedRouteId!);
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Chainage failed'),
    });
  }

  get routeOriginKm(): number {
    return (this.selectedRoute?.origin_measure_m || 0) / 1000;
  }

  get routeLengthKm(): number {
    return (this.selectedRoute?.length_m || 0) / 1000;
  }

  get routeEndKm(): number {
    return this.routeOriginKm + this.routeLengthKm;
  }

  syncRouteEditFields(): void {
    if (!this.selectedRoute) return;
    this.routeEditCode = this.selectedRoute.route_code || '';
    this.routeEditName = this.selectedRoute.route_name || '';
    this.routeEditOriginKm = (this.selectedRoute.origin_measure_m || 0) / 1000;
    this.routeTargetLengthKm = this.routeLengthKm;
    this.routeEditDesignSpeed = this.selectedRoute.design_speed_kmh ?? null;
    this.routeEditTerrain = this.selectedRoute.terrain_type || '';
    this.routeEditRowWidth = this.selectedRoute.row_width_m ?? null;
    this.routeEditCarriageway = this.selectedRoute.carriageway_config || '';
    this.routeEditClass = (this.selectedRoute.route_class as typeof this.routeEditClass) || '';
  }

  private refreshRouteAfterLengthChange(id: number, res: LrsRouteExtensionResult): void {
    const delta = res.delta_m ?? res.extend_m;
    const verb = delta >= 0 ? 'Lengthened' : 'Shortened';
    this.statusMessage = `${verb} ${res.position} by ${Math.abs(delta)} m (${res.events_affected} events)`;
    this.lrs.listRoutes(this.projectId ? { project_id: this.projectId } : undefined).subscribe({
      next: (r) => {
        this.routes = r.data || [];
        this.selectedRoute = this.routes.find((x) => x.id === id) || this.selectedRoute;
        this.syncRouteEditFields();
        this.loadRouteMap(id);
        this.lrs.listRouteExtensions(id).subscribe({
          next: (ex) => (this.routeExtensions = ex.data || []),
        });
      },
    });
  }

  applyRouteLengthAdjust(): void {
    if (!this.selectedRouteId || this.extendMeters <= 0) return;
    const deltaM = this.routeLengthMode === 'increase' ? this.extendMeters : -this.extendMeters;
    const behavior =
      this.extendBehavior === 'default'
        ? this.routeLengthMode === 'increase'
          ? 'cover'
          : this.extendPosition === 'start'
            ? 'shift'
            : 'retire'
        : this.extendBehavior;
    this.lrs
      .adjustRouteLength(this.selectedRouteId, {
        position: this.extendPosition,
        delta_m: deltaM,
        event_behavior: behavior,
      })
      .subscribe({
        next: (res) => this.refreshRouteAfterLengthChange(this.selectedRouteId!, res.data),
        error: (e) => (this.statusMessage = e?.error?.error || 'Length adjust failed'),
      });
  }

  applyRouteTargetLength(): void {
    if (!this.selectedRouteId || this.routeTargetLengthKm == null || this.routeTargetLengthKm <= 0) return;
    const behavior =
      this.extendBehavior === 'default' ? 'cover' : this.extendBehavior;
    this.lrs
      .setRouteTargetLength(this.selectedRouteId, {
        target_length_km: this.routeTargetLengthKm,
        position: this.extendPosition,
        event_behavior: behavior,
      })
      .subscribe({
        next: (res) => {
          if (res.data.unchanged) {
            this.statusMessage = 'Route length already matches target';
            return;
          }
          this.refreshRouteAfterLengthChange(this.selectedRouteId!, res.data);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Set length failed'),
      });
  }

  saveRouteMetadata(): void {
    if (!this.selectedRouteId) return;
    this.lrs
      .updateRoute(this.selectedRouteId, {
        route_code: this.routeEditCode.trim(),
        route_name: this.routeEditName.trim(),
        origin_measure_m: this.routeEditOriginKm * 1000,
        design_speed_kmh: this.routeEditDesignSpeed ?? null,
        terrain_type: this.routeEditTerrain || null,
        row_width_m: this.routeEditRowWidth ?? null,
        carriageway_config: this.routeEditCarriageway || null,
        route_class: this.routeEditClass || null,
      })
      .subscribe({
        next: (res) => {
          const id = this.selectedRouteId!;
          this.selectedRoute = res.data;
          this.lrs.listRoutes(this.projectId ? { project_id: this.projectId } : undefined).subscribe({
            next: (r) => {
              this.routes = r.data || [];
              this.selectedRoute = this.routes.find((x) => x.id === id) || res.data;
              this.syncRouteEditFields();
              this.statusMessage = 'Route metadata saved';
            },
          });
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Save route failed'),
      });
  }

  syncRouteLengthFromGeometry(): void {
    if (!this.selectedRouteId) return;
    this.lrs.updateRoute(this.selectedRouteId, { sync_length_from_geometry: true }).subscribe({
      next: (res) => {
        const id = this.selectedRouteId!;
        this.selectedRoute = res.data;
        this.syncRouteEditFields();
        this.lrs.listRoutes(this.projectId ? { project_id: this.projectId } : undefined).subscribe({
          next: (r) => {
            this.routes = r.data || [];
            this.selectedRoute = this.routes.find((x) => x.id === id) || res.data;
            this.loadRouteMap(id);
            this.statusMessage = `Length synced from geometry (${(res.data.length_m / 1000).toFixed(3)} km)`;
          },
        });
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Sync length failed'),
    });
  }

  runClashDetection(): void {
    if (!this.selectedRouteId) return;
    this.lrs
      .detectClashes(this.selectedRouteId, this.segFromKm, this.segToKm, {
        max_depth_m: this.clashMaxDepthM,
        min_vertical_sep_m: this.clashMinVerticalSepM,
        min_3d_sep_m: this.clashMin3dSepM,
      })
      .subscribe({
        next: (res) => {
          this.clashReport = res.data as LrsClashReport;
          this.statusMessage = this.clashReport.recommendation;
          this.applyClashSymbology(this.clashReport);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Clash detection failed'),
      });
  }

  /** UNM excavation permit risk assessment (graph + containment + downstream trace). */
  runUnmPermitAssess(): void {
    if (!this.selectedRouteId) return;
    this.unmAssessing = true;
    this.unmPermitResult = null;
    this.unm
      .assessPermit({
        routeId: this.selectedRouteId,
        from_km: this.segFromKm,
        to_km: this.segToKm,
        width_m: this.unmPermitWidthM,
        depth_m: this.clashMaxDepthM,
        safety_m: 10,
        persist: true,
      })
      .subscribe({
        next: (res) => {
          this.unmAssessing = false;
          this.unmPermitResult = res.data;
          this.statusMessage = `UNM risk ${res.data.riskLevel} (score ${res.data.riskScore})`;
          if (this.selectedRouteId) {
            this.lrs.listExcavationPermits(this.selectedRouteId).subscribe({
              next: (p) => (this.excavationPermits = p.data || []),
              error: () => undefined,
            });
          }
        },
        error: (e) => {
          this.unmAssessing = false;
          this.statusMessage = e?.error?.error || 'UNM permit assess failed';
        },
      });
  }

  loadUnmPanel(): void {
    this.unm.listDomains().subscribe({
      next: (res) => {
        this.unmDomains = res.data || [];
        if (!this.unmDomainId && this.unmDomains.length) {
          const telecom = this.unmDomains.find((d) => d.domain_name === 'Telecom');
          this.unmDomainId = telecom?.domain_id ?? this.unmDomains[0].domain_id;
        }
        this.onUnmDomainChange();
      },
    });
    this.refreshUnmAssets();
  }

  onUnmDomainChange(): void {
    const tiers = this.unmSelectedDomainTiers;
    if (tiers.length && !tiers.some((t) => t.tier_id === this.unmTierId)) {
      const service = tiers.find((t) => t.tier_name === 'SERVICE');
      this.unmTierId = service?.tier_id ?? tiers[tiers.length - 1].tier_id;
    }
    this.unmProperties = {};
  }

  onUnmAssetTypeChange(): void {
    this.unmProperties = {};
  }

  refreshUnmAssets(): void {
    if (!this.selectedRouteId) return;
    this.unm.listAssets({ route_id: this.selectedRouteId, limit: 500 }).subscribe({
      next: (res) => {
        this.unmAssets = res.data || [];
        this.drawUnmAssetsOnMap();
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Failed to load UNM assets'),
    });
    this.unm.listEdges({ route_id: this.selectedRouteId }).subscribe({
      next: (res) => {
        this.unmEdges = res.data || [];
        this.drawUnmNetworkOnMap();
      },
      error: () => (this.unmEdges = []),
    });
  }

  private unmTierName(asset: UnmAsset): string {
    return (asset.tier_name || 'SERVICE').toUpperCase();
  }

  private styleUnmFeature(f: FeatureLike): Style {
    const g = f.getGeometry();
    const pick = String(f.get('pick') || '');
    const traced = !!f.get('traced');
    const isolator = !!f.get('is_isolator');
    const tier = String(f.get('tier') || 'SERVICE');
    const color = isolator
      ? '#f59e0b'
      : pick === 'from'
        ? '#0ea5e9'
        : pick === 'to'
          ? '#a855f7'
          : this.unm.getTierStyleColor(tier);
    if (g instanceof LineString) {
      return new Style({
        stroke: new Stroke({
          color: traced ? '#f97316' : color,
          width: traced ? 5 : 3,
          lineDash: traced ? undefined : [8, 6],
        }),
      });
    }
    return new Style({
      image: new CircleStyle({
        radius: traced || pick ? 10 : isolator ? 9 : 7,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#fff', width: traced || pick ? 3 : 1.5 }),
      }),
    });
  }

  private drawUnmAssetsOnMap(): void {
    this.unmSource.clear();
    const format = new GeoJSON();
    const tracedIds = new Set(this.unmTraceResults.map((t) => t.node_id));
    for (const a of this.unmAssets) {
      if (!a.geom_geojson) continue;
      try {
        const feats = format.readFeatures(
          {
            type: 'Feature',
            geometry: a.geom_geojson,
            properties: {
              ...a,
              unm: true,
              tier: this.unmTierName(a),
              pick:
                a.node_id === this.unmFromNodeId
                  ? 'from'
                  : a.node_id === this.unmToNodeId
                    ? 'to'
                    : '',
              traced: a.node_id != null && tracedIds.has(a.node_id),
            },
          },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        );
        for (const f of feats) {
          f.set('unm', true);
          f.set('node_id', a.node_id);
          f.set('asset_id', a.asset_id);
          f.set('tier', this.unmTierName(a));
          this.unmSource.addFeature(f);
        }
      } catch {
        /* ignore bad geom */
      }
    }
    this.drawUnmNetworkOnMap();
  }

  private drawUnmNetworkOnMap(): void {
    this.unmTraceSource.clear();
    const format = new GeoJSON();
    const tracedNodes = new Set(this.unmTraceResults.map((t) => t.node_id));
    const overlayEdges = this.unmTraceEdges.length ? this.unmTraceEdges : this.unmEdges;
    for (const e of overlayEdges) {
      if (!e.geom_geojson) continue;
      try {
        const feats = format.readFeatures(
          { type: 'Feature', geometry: e.geom_geojson, properties: { unm_edge: true } },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        );
        for (const f of feats) {
          const traced =
            tracedNodes.has(e.from_node_id) && tracedNodes.has(e.to_node_id);
          f.set('traced', traced);
          f.set('tier', 'DISTRIBUTION');
          this.unmTraceSource.addFeature(f);
        }
      } catch {
        /* ignore */
      }
    }
    if (this.unmTraceResults.length >= 2) {
      const byDepth = [...this.unmTraceResults].sort((a, b) => a.depth - b.depth);
      for (let i = 1; i < byDepth.length; i++) {
        const a = this.unmAssets.find((x) => x.node_id === byDepth[i - 1].node_id);
        const b = this.unmAssets.find((x) => x.node_id === byDepth[i].node_id);
        const ga = a?.geom_geojson as GeoJSON.Point | undefined;
        const gb = b?.geom_geojson as GeoJSON.Point | undefined;
        if (!ga?.coordinates || !gb?.coordinates) continue;
        const line = new Feature({
          geometry: new LineString([
            fromLonLat(ga.coordinates as [number, number]),
            fromLonLat(gb.coordinates as [number, number]),
          ]),
        });
        line.set('traced', true);
        this.unmTraceSource.addFeature(line);
      }
    }
  }

  saveUnmAsset(): void {
    if (!this.selectedRouteId || !this.unmDomainId) {
      this.statusMessage = 'Select a route and domain first';
      return;
    }
    const code = this.unmAssetCode || `UNM-${Date.now()}`;
    const properties: Record<string, unknown> = {};
    for (const field of this.unmPropertyFields) {
      const v = this.unmProperties[field.name];
      if (v !== undefined && v !== null && v !== '') properties[field.name] = v;
    }
    this.unm
      .createAsset({
        domain_id: this.unmDomainId,
        tier_id: this.unmTierId,
        asset_type: this.unmAssetType,
        asset_code: code,
        asset_name: this.unmAssetName || code,
        route_id: this.selectedRouteId,
        chainage_km: this.unmChainageKm || this.measureKm,
        offset_distance: Math.abs(this.unmOffsetM),
        offset_side: this.unmOffsetSide,
        depth_m: this.unmDepthM,
        properties,
      })
      .subscribe({
        next: () => {
          this.statusMessage = 'UNM asset created (LRS utility twin synced)';
          this.refreshUnmAssets();
          if (this.selectedRouteId) this.loadLifecycleData(this.selectedRouteId);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Create UNM asset failed'),
      });
  }

  connectUnmNodes(): void {
    if (!this.unmFromNodeId || !this.unmToNodeId) {
      this.statusMessage = 'Select from/to node IDs';
      return;
    }
    const fromAsset = this.unmAssets.find((a) => a.node_id === this.unmFromNodeId);
    const toAsset = this.unmAssets.find((a) => a.node_id === this.unmToNodeId);
    if (fromAsset && toAsset) {
      const ok = this.unm.validateConnectionRulesClient(fromAsset.asset_type, toAsset.asset_type);
      if (!ok) {
        this.statusMessage = `Client-side rule: cannot connect ${fromAsset.asset_type} → ${toAsset.asset_type}`;
        return;
      }
    }
    this.unm
      .connect({
        fromNodeId: this.unmFromNodeId,
        toNodeId: this.unmToNodeId,
        flowDirection: 'Bidirectional',
      })
      .subscribe({
        next: (res) => {
          this.statusMessage = res.message || 'Connection created';
          const topo = (res.data as { topology?: { loopDetected?: boolean } })?.topology;
          if (topo?.loopDetected) this.statusMessage += ' (warning: loop detected)';
          this.unmPickStep = 'from';
          this.refreshUnmAssets();
        },
        error: (e) =>
          (this.statusMessage = e?.error?.message || e?.error?.error || 'Connect failed'),
      });
  }

  runUnmTrace(): void {
    if (!this.unmTraceNodeId) {
      this.statusMessage = 'Select a start node for trace';
      return;
    }
    this.unm.trace(this.unmTraceNodeId, this.unmTraceDirection).subscribe({
      next: (res) => {
        this.unmTraceResults = res.trace_results || [];
        this.unmTraceEdges = res.trace_edges || [];
        this.statusMessage = `Trace (${this.unmTraceDirection}) returned ${this.unmTraceResults.length} node(s)`;
        this.drawUnmAssetsOnMap();
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Trace failed'),
    });
  }

  saveUnmContainment(): void {
    if (!this.unmContainerAssetId || !this.unmContainedAssetId) return;
    this.unm.addContainment(this.unmContainerAssetId, this.unmContainedAssetId).subscribe({
      next: () => (this.statusMessage = 'Containment association saved'),
      error: (e) => (this.statusMessage = e?.error?.error || 'Containment failed'),
    });
  }

  saveUnmAttachment(): void {
    if (!this.unmStructureAssetId || !this.unmAttachedAssetId) return;
    this.unm.addAttachment(this.unmStructureAssetId, this.unmAttachedAssetId).subscribe({
      next: () => (this.statusMessage = 'Attachment association saved'),
      error: (e) => (this.statusMessage = e?.error?.error || 'Attachment failed'),
    });
  }

  pickUnmAssetFromList(a: UnmAsset, role: 'from' | 'to' | 'trace' | 'container' | 'contained' | 'structure' | 'attached'): void {
    if (!a.node_id && role !== 'container' && role !== 'contained' && role !== 'structure' && role !== 'attached') {
      this.statusMessage = 'Asset has no graph node';
      return;
    }
    if (role === 'from') this.unmFromNodeId = a.node_id ?? null;
    if (role === 'to') this.unmToNodeId = a.node_id ?? null;
    if (role === 'trace') this.unmTraceNodeId = a.node_id ?? null;
    if (role === 'container') this.unmContainerAssetId = a.asset_id;
    if (role === 'contained') this.unmContainedAssetId = a.asset_id;
    if (role === 'structure') this.unmStructureAssetId = a.asset_id;
    if (role === 'attached') this.unmAttachedAssetId = a.asset_id;
    this.drawUnmAssetsOnMap();
  }

  private handleUnmMapPick(asset: UnmAsset): void {
    if (!asset.node_id) {
      this.statusMessage = `${asset.asset_code} has no graph node`;
      return;
    }
    this.unmTraceNodeId = asset.node_id;
    if (!this.unmConnectPickMode) {
      this.statusMessage = `Selected ${asset.asset_type} node ${asset.node_id}`;
      this.drawUnmAssetsOnMap();
      return;
    }
    if (this.unmPickStep === 'from') {
      this.unmFromNodeId = asset.node_id;
      this.unmPickStep = 'to';
      this.statusMessage = `From ${asset.asset_type} #${asset.node_id} — click a second asset to connect`;
      this.drawUnmAssetsOnMap();
      return;
    }
    if (asset.node_id === this.unmFromNodeId) {
      this.statusMessage = 'Pick a different asset as the connection target';
      return;
    }
    this.unmToNodeId = asset.node_id;
    this.drawUnmAssetsOnMap();
    this.connectUnmNodes();
  }

  setActivePanel(panel: LrsWorkbenchPanel): void {
    if (this.activePanel === 'cadastral' && panel !== 'cadastral') this.stopCadastralDraw();
    this.activePanel = panel;
    this.cadastralLayer?.setVisible(panel === 'cadastral');
    if (panel === 'unm') this.loadUnmPanel();
    if (panel === 'alignment' && this.selectedRouteId) this.loadAlignment(this.selectedRouteId);
    this.persistSidebarLayout();
  }

  onCadastralParcels(parcels: LrsLandParcel[]): void {
    this.cadastralSource.clear();
    const fmt = new GeoJSON();
    const feats: Feature[] = [];
    for (const p of parcels) {
      if (!p.geometry) continue;
      const f = fmt.readFeature(
        { type: 'Feature', geometry: p.geometry, properties: {} },
        { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
      ) as Feature;
      f.setProperties({ parcel_id: p.id, parcel_code: p.parcel_code, status: p.status });
      feats.push(f);
    }
    this.cadastralSource.addFeatures(feats);
    if (this.cadastralSelectedParcelId && !parcels.some((p) => p.id === this.cadastralSelectedParcelId)) {
      this.cadastralSelectedParcelId = null;
    }
    this.cadastralLayer?.changed();
  }

  onCadastralSelect(id: number | null): void {
    this.cadastralSelectedParcelId = id;
    this.cadastralLayer?.changed();
  }

  onCadastralZoom(p: LrsLandParcel): void {
    if (!this.map) return;
    const feat = this.cadastralSource.getFeatures().find((f) => f.get('parcel_id') === p.id);
    const geom = feat?.getGeometry();
    if (!geom) return;
    this.map.getView().fit(extentBuffer(geom.getExtent(), 120), { duration: 350, maxZoom: 18 });
  }

  onCadastralDrawToggle(on: boolean): void {
    if (!on) {
      this.stopCadastralDraw();
      return;
    }
    if (!this.map || this.cadastralDraw) return;
    if (this.drawMode) this.toggleDrawRoute();
    if (this.realignDrawMode) this.toggleRealignRoute();
    this.cadastralDrawnGeometry = null;
    this.cadastralDraw = new Draw({ source: this.cadastralDrawSource, type: 'Polygon' });
    this.cadastralDraw.on('drawend', (evt) => {
      const poly = evt.feature.getGeometry() as Polygon;
      this.cadastralDrawnGeometry = {
        type: 'Polygon',
        coordinates: poly.getCoordinates().map((ring) => ring.map((c) => toLonLat(c))),
      };
      setTimeout(() => this.stopCadastralDraw());
    });
    this.map.addInteraction(this.cadastralDraw);
    this.cadastralDrawActive = true;
    this.statusMessage = 'Draw parcel boundary — double-click to finish';
  }

  private stopCadastralDraw(): void {
    if (this.cadastralDraw) {
      this.cadastralDraw.setActive(false);
      this.map?.removeInteraction(this.cadastralDraw);
      this.cadastralDraw = undefined;
    }
    this.cadastralDrawSource.clear();
    this.cadastralDrawActive = false;
  }

  private styleCadastralParcel(f: FeatureLike, resolution: number): Style[] {
    const status = String(f.get('status')) as LrsParcelStatus;
    const color = PARCEL_STATUS_META[status]?.color || '#64748b';
    const selected = Number(f.get('parcel_id')) === this.cadastralSelectedParcelId;
    return [
      new Style({
        fill: new Fill({ color: `${color}${selected ? '66' : '38'}` }),
        stroke: new Stroke({ color: selected ? '#1d4ed8' : color, width: selected ? 3 : 1.6 }),
        text:
          resolution <= 4
            ? new OlText({
                text: String(f.get('parcel_code') || ''),
                font: '600 10px sans-serif',
                fill: new Fill({ color: '#0f172a' }),
                stroke: new Stroke({ color: '#fff', width: 3 }),
                overflow: true,
              })
            : undefined,
      }),
    ];
  }

  loadAlignment(routeId: number): void {
    this.lrs.listVerticalPvis(routeId).subscribe({
      next: (res) => (this.verticalPvis = res.data || []),
      error: () => (this.verticalPvis = []),
    });
    this.lrs.listGroundProfile(routeId).subscribe({
      next: (res) => (this.groundSamples = res.data || []),
      error: () => (this.groundSamples = []),
    });
  }

  pviComputedK(p: {
    k_value?: number | null;
    curve_length_m?: number;
    grade_in_pct?: number;
    grade_out_pct?: number;
  }): number | null {
    if (p.k_value != null && Number.isFinite(Number(p.k_value))) return Number(p.k_value);
    const A = Number(p.grade_out_pct || 0) - Number(p.grade_in_pct || 0);
    if (Math.abs(A) < 1e-9) return null;
    return Number(p.curve_length_m || 0) / Math.abs(A);
  }

  beginEditPvi(p: LrsVerticalPvi): void {
    this.pviEditId = p.id;
    this.pviMeasureKm = p.pvi_measure_m / 1000;
    this.pviElevM = p.pvi_elevation_m;
    this.pviGradeIn = p.grade_in_pct;
    this.pviGradeOut = p.grade_out_pct;
    this.pviCurveLengthM = p.curve_length_m;
    this.pviLabel = p.label || '';
  }

  clearPviForm(): void {
    this.pviEditId = null;
    this.pviLabel = `VPI${this.verticalPvis.length + 1}`;
  }

  savePvi(): void {
    if (!this.selectedRouteId) return;
    const body = {
      pvi_measure_km: this.pviMeasureKm,
      pvi_elevation_m: this.pviElevM,
      grade_in_pct: this.pviGradeIn,
      grade_out_pct: this.pviGradeOut,
      curve_length_m: this.pviCurveLengthM,
      label: this.pviLabel || undefined,
    };
    const req = this.pviEditId
      ? this.lrs.updateVerticalPvi(this.pviEditId, body)
      : this.lrs.createVerticalPvi(this.selectedRouteId, body);
    req.subscribe({
      next: () => {
        this.alignmentStatus = this.pviEditId ? 'PVI updated' : 'PVI saved';
        this.clearPviForm();
        this.loadAlignment(this.selectedRouteId!);
        this.dashboard.markDirty();
      },
      error: (err: { error?: { error?: string } }) => {
        this.alignmentStatus = err?.error?.error || 'Failed to save PVI';
      },
    });
  }

  deletePvi(id: number): void {
    this.lrs.deleteVerticalPvi(id).subscribe({
      next: () => {
        if (this.pviEditId === id) this.clearPviForm();
        this.alignmentStatus = 'PVI deleted';
        if (this.selectedRouteId) this.loadAlignment(this.selectedRouteId);
      },
      error: () => (this.alignmentStatus = 'Failed to delete PVI'),
    });
  }

  addGroundSample(): void {
    if (!this.selectedRouteId) return;
    const samples = this.groundSamples.map((s) => ({
      measure_m: s.measure_m,
      elevation_m: s.elevation_m,
      source: s.source || 'survey',
    }));
    samples.push({
      measure_m: this.groundMeasureKm * 1000,
      elevation_m: this.groundElevM,
      source: this.groundSource,
    });
    samples.sort((a, b) => a.measure_m - b.measure_m);
    this.lrs.replaceGroundProfile(this.selectedRouteId, { samples, replace: true }).subscribe({
      next: () => {
        this.alignmentStatus = 'Ground sample added';
        this.loadAlignment(this.selectedRouteId!);
      },
      error: () => (this.alignmentStatus = 'Failed to save ground sample'),
    });
  }

  removeGroundSample(sample: LrsGroundSample): void {
    if (!this.selectedRouteId) return;
    const samples = this.groundSamples
      .filter((s) => s !== sample)
      .map((s) => ({
        measure_m: s.measure_m,
        elevation_m: s.elevation_m,
        source: s.source || 'survey',
      }));
    this.lrs.replaceGroundProfile(this.selectedRouteId, { samples, replace: true }).subscribe({
      next: () => {
        this.alignmentStatus = 'Ground sample removed';
        this.loadAlignment(this.selectedRouteId!);
      },
      error: () => (this.alignmentStatus = 'Failed to remove ground sample'),
    });
  }

  private handleDashboardAction(action: LrsQuickAction): void {
    if (!this.selectedRouteId) {
      this.statusMessage = 'Select an LRS route first';
      return;
    }
    if (this.sidebarPanelOnly && action !== 'take-photo' && action !== 'upload-file') {
      this.pickerOpen = false;
      this.setSidebarMode('workbench');
    }
    switch (action) {
      case 'add-asset':
        this.setActivePanel('assets');
        this.statusMessage = 'Pick an asset type, set chainage, then save';
        break;
      case 'add-utility':
        this.setActivePanel('utilities');
        this.statusMessage = 'Pick a utility type, set offset/depth, then save';
        break;
      case 'add-structure':
        this.setActivePanel('assets');
        this.selectAssetType('culvert');
        this.statusMessage = 'Structure: culvert selected — set chainage and save, or pick bridge / underpass';
        break;
      case 'draw-corridor':
        this.setActivePanel('corridor');
        if (!this.integratedMode || this.mapInteractActive) this.toggleDrawRoute();
        else this.statusMessage = 'Corridor panel open — draw the route on the map';
        break;
      case 'upload-file':
      case 'take-photo':
        if (!this.selectedAssetId) {
          this.statusMessage = 'Select an asset on the map first';
        }
        break;
      case 'generate-report':
        this.setActivePanel('reports');
        this.previewLrsReport();
        break;
      case 'export-data':
        this.setActivePanel('reports');
        this.exportLrsReport();
        break;
      case 'open-alignment':
        this.setActivePanel('alignment');
        this.statusMessage = 'Edit PVIs and existing-ground samples, then save';
        break;
    }
  }

  private clashSeverityColor(severity: string): string {
    switch (severity) {
      case 'critical':
        return '#7f1d1d';
      case 'high':
        return '#dc2626';
      case 'warning':
        return '#eab308';
      case 'medium':
        return '#f59e0b';
      default:
        return '#64748b';
    }
  }

  private applyClashSymbology(report: LrsClashReport): void {
    this.clashSource.clear();
    if (report.zone_geometry) {
      const zoneFeats = this.geoJson.readFeatures(
        { type: 'Feature', geometry: report.zone_geometry, properties: { layer: 'excavation_zone' } },
        { featureProjection: 'EPSG:3857' }
      );
      this.segPreviewSource.clear();
      this.segPreviewSource.addFeatures(zoneFeats);
    }
    const fc = report.clash_points;
    if (fc?.features?.length) {
      const feats = this.geoJson.readFeatures(fc, { featureProjection: 'EPSG:3857' });
      for (const f of feats) {
        f.set('severity', f.get('severity') || 'info');
      }
      this.clashSource.addFeatures(feats);
    }
    const src = this.clashSource.getFeatures().length ? this.clashSource : this.segPreviewSource;
    if (src.getFeatures().length) this.fitToSource(src);
  }

  clearOffsetPreview(): void {
    this.offsetLineSource.clear();
    this.offsetConnectorSource.clear();
  }

  toggleEditRouteVertices(): void {
    if (!this.map || !this.selectedRouteId) return;
    if (this.drawMode) this.toggleDrawRoute();
    if (this.realignDrawMode) this.toggleRealignRoute();
    this.routeEditVerticesMode = !this.routeEditVerticesMode;
    if (this.routeEditVerticesMode) {
      if (!this.routeSource.getFeatures().length) {
        this.routeEditVerticesMode = false;
        this.statusMessage = 'Load a route on the map first';
        return;
      }
      this.routeModify = new Modify({ source: this.routeSource });
      this.routeSnap = new Snap({ source: this.routeSource });
      this.map.addInteraction(this.routeModify);
      this.map.addInteraction(this.routeSnap);
      this.statusMessage = 'Drag vertices to shape curves; save centerline when done';
    } else {
      this.teardownRouteModify();
      this.statusMessage = 'Vertex edit off';
    }
  }

  private teardownRouteModify(): void {
    if (this.routeModify && this.map) {
      this.map.removeInteraction(this.routeModify);
      this.routeModify = undefined;
    }
    if (this.routeSnap && this.map) {
      this.map.removeInteraction(this.routeSnap);
      this.routeSnap = undefined;
    }
    this.routeEditVerticesMode = false;
  }

  densifyRouteVertices(): void {
    const feat = this.routeSource.getFeatures()[0];
    if (!feat) return;
    const line = feat.getGeometry() as LineString;
    const coords = line.getCoordinates();
    if (coords.length < 2) return;
    const dense: number[][] = [];
    const steps = 4;
    for (let i = 0; i < coords.length - 1; i++) {
      dense.push(coords[i]);
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        dense.push([
          coords[i][0] * (1 - t) + coords[i + 1][0] * t,
          coords[i][1] * (1 - t) + coords[i + 1][1] * t,
        ]);
      }
    }
    dense.push(coords[coords.length - 1]);
    line.setCoordinates(dense);
    this.statusMessage = 'Added vertices along segments — drag for smoother curves';
  }

  saveEditedRouteGeometry(): void {
    const feat = this.routeSource.getFeatures()[0];
    if (!feat || !this.selectedRouteId) return;
    const geom = feat.getGeometry() as LineString;
    const coords = geom.getCoordinates().map((c) => toLonLat(c));
    this.teardownRouteModify();
    this.submitRealign({ type: 'LineString', coordinates: coords });
  }

  toggleRealignRoute(): void {
    if (!this.map || !this.selectedRouteId) return;
    if (this.routeEditVerticesMode) this.teardownRouteModify();
    this.realignDrawMode = !this.realignDrawMode;
    if (this.drawMode) this.toggleDrawRoute();
    if (this.realignDrawMode) {
      this.realignDraw = new Draw({ source: this.routeSource, type: 'LineString' });
      this.realignDraw.on('drawend', (evt) => {
        const geom = evt.feature.getGeometry() as LineString;
        const coords = geom.getCoordinates().map((c) => toLonLat(c));
        this.submitRealign({ type: 'LineString', coordinates: coords });
        this.realignDrawMode = false;
        this.realignDraw?.setActive(false);
        this.map?.removeInteraction(this.realignDraw!);
        this.realignDraw = undefined;
      });
      this.map.addInteraction(this.realignDraw);
      this.statusMessage = 'Draw new centerline to realign route';
    } else if (this.realignDraw) {
      this.map.removeInteraction(this.realignDraw);
      this.realignDraw = undefined;
    }
  }

  private submitRealign(geometry: GeoJSON.LineString): void {
    if (!this.selectedRouteId) return;
    if (!confirm('Replace route centerline? Event measures are unchanged — run QC after realign.')) return;
    this.lrs.realignRoute(this.selectedRouteId, { geometry }).subscribe({
      next: (res) => {
        this.statusMessage = `Realigned: ${(res.data.previous_length_m / 1000).toFixed(2)} → ${(res.data.new_length_m / 1000).toFixed(2)} km`;
        this.lrs.listRoutes(this.projectId ? { project_id: this.projectId } : undefined).subscribe({
          next: (r) => {
            const id = this.selectedRouteId!;
            this.routes = r.data || [];
            this.selectedRoute = this.routes.find((x) => x.id === id) || this.selectedRoute;
            this.loadRouteMap(id);
            this.loadLifecycleData(id);
            this.runRouteQc();
          },
        });
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Realign failed'),
    });
  }

  saveExcavationPermit(): void {
    if (!this.selectedRouteId) return;
    this.lrs
      .createExcavationPermit(this.selectedRouteId, {
        from_measure_km: this.segFromKm,
        to_measure_km: this.segToKm,
        max_depth_m: this.clashMaxDepthM,
        depth_m: this.clashMaxDepthM,
      })
      .subscribe({
        next: (res) => {
          this.clashReport = res.data.clash_report;
          this.statusMessage = `Permit saved — ${res.data.clash_report.recommendation}`;
          this.applyClashSymbology(res.data.clash_report);
          if (this.selectedRouteId) {
            this.lrs.listExcavationPermits(this.selectedRouteId).subscribe({
              next: (p) => (this.excavationPermits = p.data || []),
            });
          }
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Permit save failed'),
      });
  }

  runAssetsNearGps(): void {
    if (!this.selectedRouteId) return;
    const lng = this.gpsLng ?? this.locateResult?.point?.coordinates?.[0];
    const lat = this.gpsLat ?? this.locateResult?.point?.coordinates?.[1];
    if (lng == null || lat == null) {
      this.statusMessage = 'Set GPS coordinates or click the route on the map';
      return;
    }
    this.lrs.assetsNearPoint(this.selectedRouteId, lng, lat, this.fieldRadiusM).subscribe({
      next: (res) => {
        this.nearbyItems = res.data.items || [];
        this.locateResult = res.data.locate;
        this.measureKm = res.data.locate.measure_km;
        if (res.data.reference) this.referenceInput = res.data.reference;
        this.statusMessage = `${res.data.count} assets within ${res.data.radius_m}m along route`;
        const pt = res.data.locate.closest_point || res.data.locate.point;
        if (pt?.coordinates) {
          const [plng, plat] = pt.coordinates as [number, number];
          this.showMarker(plng, plat, res.data.locate.chainage_label);
        }
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Nearby search failed'),
    });
  }

  /** Signed lateral offset (m) for API from side + magnitude. */
  effectiveLateralOffsetM(side: string, offsetM: number): number {
    let o = Number(offsetM) || 0;
    const s = (side || '').toUpperCase();
    if (s === 'C') return 0;
    const mag = Math.abs(o);
    if (s === 'L') return mag > 0 ? -mag : o < 0 ? o : -2;
    if (s === 'R') return mag > 0 ? mag : o > 0 ? o : 2;
    return o;
  }

  reapplyAssetOffsets(): void {
    if (!this.selectedRouteId) return;
    if (!confirm('Reposition all assets using their side and offset values?')) return;
    this.lrs.reapplyEventOffsets(this.selectedRouteId).subscribe({
      next: (res) => {
        this.statusMessage = `Repositioned ${res.data.events_updated} asset(s)`;
        this.loadRouteMap(this.selectedRouteId!);
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Reapply offsets failed'),
    });
  }

  previewOffsetGeometry(): void {
    if (!this.selectedRouteId) return;
    const offset = this.effectiveLateralOffsetM(this.assetSide, this.lateralOffsetM);
    this.lrs.geometryAtOffset(this.selectedRouteId, this.measureKm, offset, this.assetSide || undefined).subscribe({
      next: (res) => {
        this.clearOffsetPreview();
        this.markerSource.clear();
        const center = res.data.center_point;
        const off = res.data.offset_point;
        let centerCoord: [number, number] | null = null;
        if (center?.coordinates) {
          centerCoord = center.coordinates as [number, number];
          this.markerSource.addFeature(
            new Feature({
              geometry: new Point(fromLonLat(centerCoord)),
              label: `${res.data.chainage_label} (center)`,
            })
          );
        }
        if (res.data.offset_line?.coordinates?.length) {
          const lineFeats = this.geoJson.readFeatures(
            { type: 'Feature', geometry: res.data.offset_line, properties: {} },
            { featureProjection: 'EPSG:3857' }
          );
          this.offsetLineSource.addFeatures(lineFeats);
        }
        if (off?.coordinates) {
          const offCoord = off.coordinates as [number, number];
          this.markerSource.addFeature(
            new Feature({
              geometry: new Point(fromLonLat(offCoord)),
              label: `offset ${offset}m`,
            })
          );
          if (centerCoord) {
            const c3857 = fromLonLat(centerCoord);
            const o3857 = fromLonLat(offCoord);
            this.offsetConnectorSource.addFeature(
              new Feature({ geometry: new LineString([c3857, o3857]) })
            );
          }
        }
        const fitSrc =
          this.offsetLineSource.getFeatures().length > 0
            ? this.offsetLineSource
            : this.markerSource;
        if (fitSrc.getFeatures().length) this.fitToSource(fitSrc);
        this.statusMessage = `Offset preview @ ${res.data.chainage_label}`;
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Offset geometry failed'),
    });
  }

  retireAssetEvent(eventId: number): void {
    if (!confirm('Retire this asset event (set valid_to)?')) return;
    this.lrs.retireEvent(eventId).subscribe({
      next: () => {
        this.statusMessage = 'Event retired';
        this.closeAssetDetail();
        if (this.selectedRouteId) this.loadRouteMap(this.selectedRouteId);
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Retire failed'),
    });
  }

  get editAssetIsLinear(): boolean {
    return isLinearAssetType(this.editEventType);
  }

  openAssetFromList(event: LrsEvent): void {
    this.openHighwayAssetDetail(event);
  }

  closeAssetDetail(): void {
    this.assetDetailOpen = false;
    this.selectedAssetId = null;
    this.clusterPickCandidates = null;
    this.highlightSource.clear();
    this.dashboard.selectAsset(null);
  }

  onAssetDetailSaved(): void {
    this.dashboard.markDirty();
    if (this.selectedRouteId) {
      this.loadRouteMap(this.selectedRouteId);
      this.loadLifecycleData(this.selectedRouteId);
    }
  }

  onAssetDetailRetired(): void {
    this.onAssetDetailSaved();
    this.closeAssetDetail();
  }

  openLinkedAsset(ev: { kind: 'event' | 'utility'; id: number }): void {
    if (ev.kind === 'utility') {
      const u = this.utilityAssets.find((x) => x.id === ev.id);
      if (u) {
        this.openUtilityAssetDetail(u);
        return;
      }
      this.lrs.getUtilityAsset(ev.id).subscribe({
        next: (res) => this.openUtilityAssetDetail(res.data),
        error: (err) => (this.statusMessage = err?.error?.error || 'Linked utility not found'),
      });
      return;
    }
    const e = this.assetEvents.find((x) => x.id === ev.id);
    if (e) {
      this.openHighwayAssetDetail(e);
      return;
    }
    this.lrs.getEvent(ev.id).subscribe({
      next: (res) => this.openHighwayAssetDetail(res.data),
      error: (err) => (this.statusMessage = err?.error?.error || 'Linked asset not found'),
    });
  }

  private isAssetPickLayer(layer: Layer): boolean {
    return (
      layer === this.pointClusterLayer ||
      layer === this.linearEventLayer ||
      layer === this.utilityClusterLayer ||
      layer === this.utilityLineLayer ||
      (this.activePanel === 'unm' && layer === this.unmLayer)
    );
  }

  private handleMapBrowserClick(evt: MapBrowserEvent): void {
    if (!this.map || this.drawMode || this.realignDrawMode || this.routeEditVerticesMode) return;
    if (this.cadastralDrawActive) return;
    if (this.integratedMode && !this.mapInteractActive) return;

    if (this.activePanel === 'cadastral' && this.cadastralLayer) {
      const parcelFeat = this.map.forEachFeatureAtPixel(
        evt.pixel,
        (feat) => feat,
        { hitTolerance: 4, layerFilter: (layer) => layer === this.cadastralLayer }
      ) as Feature | undefined;
      if (parcelFeat) {
        this.onCadastralSelect(Number(parcelFeat.get('parcel_id')));
        return;
      }
    }

    if (this.activePanel === 'unm' && this.unmLayer) {
      const unmFeat = this.map.forEachFeatureAtPixel(
        evt.pixel,
        (feat) => feat,
        { hitTolerance: 12, layerFilter: (layer) => layer === this.unmLayer }
      ) as Feature | undefined;
      if (unmFeat) {
        const assetId = Number(unmFeat.get('asset_id'));
        const asset = this.unmAssets.find((a) => a.asset_id === assetId);
        if (asset) {
          this.handleUnmMapPick(asset);
          return;
        }
      }
    }

    const picked = this.map.forEachFeatureAtPixel(
      evt.pixel,
      (feat) => feat,
      { hitTolerance: 10, layerFilter: (layer) => this.isAssetPickLayer(layer) }
    ) as Feature | undefined;

    if (picked && !this.placeAssetMode && !this.chainagePickActive) {
      const members = picked.get('features') as Feature[] | undefined;
      if (members && members.length > 1) {
        this.clusterPickCandidates = members
          .map((m) => this.featureToPickCandidate(m))
          .filter((c): c is NonNullable<typeof c> => !!c);
        this.assetDetailOpen = true;
        this.selectedAssetId = null;
        this.highlightSource.clear();
        return;
      }
      const target = members?.length === 1 ? members[0] : picked;
      this.openAssetFromFeature(target);
      return;
    }

    const [lng, lat] = toLonLat(evt.coordinate);
    this.onMapClick(lng, lat);
  }

  private featureToPickCandidate(
    f: Feature
  ): { id: number; label: string; type: string; kind: 'highway' | 'utility' } | null {
    const id = Number(f.get('id'));
    if (!id) return null;
    const kind = f.get('asset_kind') === 'utility' || f.get('utility_type') ? 'utility' : 'highway';
    const type = String(f.get('event_type') || f.get('utility_type') || '');
    const label = String(f.get('label') || type);
    return { id, label, type, kind };
  }

  pickClusterCandidate(c: { id: number; kind: 'highway' | 'utility' }): void {
    this.clusterPickCandidates = null;
    if (c.kind === 'utility') {
      const u = this.utilityAssets.find((x) => x.id === c.id);
      if (u) this.openUtilityAssetDetail(u);
    } else {
      const e = this.assetEvents.find((x) => x.id === c.id);
      if (e) this.openHighwayAssetDetail(e);
      else this.lrs.getEvent(c.id).subscribe({
        next: (res) => this.openHighwayAssetDetail(res.data),
        error: (err) => (this.statusMessage = err?.error?.error || 'Asset not found'),
      });
    }
  }

  private openAssetFromFeature(f: Feature): void {
    const kind = f.get('asset_kind') === 'utility' || f.get('utility_type') ? 'utility' : 'highway';
    const id = Number(f.get('id'));
    if (kind === 'utility') {
      const u = this.utilityAssets.find((x) => x.id === id);
      if (u) {
        this.openUtilityAssetDetail(u, f);
        return;
      }
    } else if (id) {
      const e = this.assetEvents.find((x) => x.id === id);
      if (e) {
        this.openHighwayAssetDetail(e, f);
        return;
      }
      this.lrs.getEvent(id).subscribe({
        next: (res) => this.openHighwayAssetDetail(res.data, f),
        error: (err) => (this.statusMessage = err?.error?.error || 'Asset not found'),
      });
      return;
    }
    this.statusMessage = 'Could not resolve asset';
  }

  private openHighwayAssetDetail(event: LrsEvent, mapFeature?: Feature): void {
    this.assetDetailKind = 'highway';
    this.selectedAssetId = event.id;
    this.clusterPickCandidates = null;
    this.editLabel = event.label || '';
    this.editEventType = event.event_type;
    this.editMeasureKm = event.measure_m / 1000;
    this.editMeasureToKm = event.measure_to_m ? event.measure_to_m / 1000 : this.editMeasureKm;
    this.editSide = event.side || '';
    const props = (event.properties || {}) as Record<string, unknown>;
    this.editConditionGrade = String(props['condition_grade'] || '');
    this.editAssetRef = String(props['asset_ref'] || '');
    this.editLateralOffsetM = Number((event as { offset_m?: number }).offset_m ?? props['offset_m'] ?? 0);
    this.editGrade = inferLrsGrade({
      grade: event.grade ?? props['grade'],
      event_type: event.event_type,
      properties: props,
    });
    this.assetDetailOpen = true;
    this.activePanel = 'assets';
    this.dashboard.selectAsset({
      kind: 'event',
      id: event.id,
      measure_m: event.measure_m,
      label: event.label,
      typeId: event.event_type,
    });
    if (event.measure_m != null) {
      this.dashboard.setChainage({
        measure_m: event.measure_m,
        label: event.chainage_label || formatChainageFromMeters(event.measure_m),
      });
    }
    this.highlightAssetFeature(mapFeature, event.geometry);
    this.measureKm = this.editMeasureKm;
    this.measureToKm = this.editMeasureToKm;
  }

  private openUtilityAssetDetail(asset: LrsUtilityAsset, mapFeature?: Feature): void {
    this.assetDetailKind = 'utility';
    this.selectedAssetId = asset.id;
    this.clusterPickCandidates = null;
    this.editLabel = asset.label || '';
    this.editUtilityType = asset.utility_type;
    this.editMeasureKm = asset.measure_m / 1000;
    this.editMeasureToKm = asset.measure_to_m ? asset.measure_to_m / 1000 : this.editMeasureKm;
    this.editUtilityDepthM = asset.depth_m ?? null;
    this.editUtilityDiameterMm = asset.diameter_mm ?? null;
    this.editGrade = inferLrsGrade(asset);
    this.assetDetailOpen = true;
    this.activePanel = 'utilities';
    this.dashboard.selectAsset({
      kind: 'utility',
      id: asset.id,
      measure_m: asset.measure_m,
      label: asset.label,
      typeId: asset.utility_type,
    });
    if (asset.measure_m != null) {
      this.dashboard.setChainage({
        measure_m: asset.measure_m,
        label: formatChainageFromMeters(asset.measure_m),
      });
    }
    this.highlightAssetFeature(mapFeature, asset.geometry);
    this.measureKm = this.editMeasureKm;
    this.measureToKm = this.editMeasureToKm;
  }

  private highlightAssetFeature(mapFeature?: Feature, geometry?: GeoJSON.Geometry): void {
    this.highlightSource.clear();
    if (mapFeature) {
      this.highlightSource.addFeature(mapFeature.clone());
    } else if (geometry) {
      const feats = this.geoJson.readFeatures(
        { type: 'Feature', geometry, properties: {} },
        { featureProjection: 'EPSG:3857' }
      );
      this.highlightSource.addFeatures(feats);
    }
    if (this.highlightSource.getFeatures().length) {
      this.fitToSource(this.highlightSource, 17);
    }
  }

  saveAssetDetail(): void {
    if (!this.selectedAssetId || this.savingAsset) return;

    if (this.assetDetailKind === 'utility') {
      const utilErr = this.validateChainageRange(
        this.editMeasureKm,
        this.editMeasureToKm > this.editMeasureKm ? this.editMeasureToKm : null,
        this.editMeasureToKm > this.editMeasureKm
      );
      if (utilErr) {
        this.chainageValidationError = utilErr;
        this.statusMessage = utilErr;
        return;
      }
    } else {
      const err = this.validateChainageRange(
        this.editMeasureKm,
        this.editAssetIsLinear ? this.editMeasureToKm : null,
        this.editAssetIsLinear
      );
      if (err) {
        this.chainageValidationError = err;
        this.statusMessage = err;
        return;
      }
    }
    this.chainageValidationError = '';
    this.savingAsset = true;

    if (this.assetDetailKind === 'utility') {
      const body: Record<string, unknown> = {
        utility_type: this.editUtilityType,
        measure_km: this.editMeasureKm,
        label: this.editLabel,
        depth_m: this.editUtilityDepthM,
        diameter_mm: this.editUtilityDiameterMm,
        grade: this.editGrade,
      };
      if (this.editMeasureToKm > this.editMeasureKm) {
        body['measure_to_km'] = this.editMeasureToKm;
      } else {
        body['measure_to_km'] = null;
      }
      this.lrs.updateUtilityAsset(this.selectedAssetId, body).subscribe({
        next: () => {
          this.savingAsset = false;
          this.statusMessage = 'Utility updated';
          if (this.selectedRouteId) {
            this.loadRouteMap(this.selectedRouteId);
            this.loadLifecycleData(this.selectedRouteId);
          }
        },
        error: (e) => {
          this.savingAsset = false;
          this.statusMessage = e?.error?.error || 'Update failed';
        },
      });
      return;
    }

    const body: Parameters<LrsService['updateEvent']>[1] = {
      label: this.editLabel,
      event_type: this.editEventType,
      measure_km: this.editMeasureKm,
      side: this.editSide || undefined,
      offset_m: this.editLateralOffsetM || undefined,
      grade: this.editGrade,
      condition_grade: this.editConditionGrade || undefined,
      asset_ref: this.editAssetRef || undefined,
      properties: {
        asset_category: 'highway',
        condition_grade: this.editConditionGrade || undefined,
        asset_ref: this.editAssetRef || undefined,
        symbol_code: symbolCodeForAssetType(this.editEventType),
        grade: this.editGrade,
      },
    };
    if (this.editAssetIsLinear) {
      body.measure_to_km = this.editMeasureToKm;
    } else {
      body.measure_to_km = null;
      body.clear_measure_to = true;
    }

    this.lrs.updateEvent(this.selectedAssetId, body).subscribe({
      next: () => {
        this.savingAsset = false;
        this.statusMessage = 'Asset updated';
        if (this.selectedRouteId) this.loadRouteMap(this.selectedRouteId);
      },
      error: (e) => {
        this.savingAsset = false;
        this.statusMessage = e?.error?.error || 'Update failed';
      },
    });
  }

  onMapClick(lng: number, lat: number): void {
    if (!this.selectedRouteId) return;
    this.gpsLng = lng;
    this.gpsLat = lat;
    this.lrs.locate(this.selectedRouteId, lng, lat).subscribe({
      next: (res) => {
        this.applyLocateResult(res.data);
        const pt = res.data.closest_point || res.data.point;
        if (pt?.coordinates) {
          const [plng, plat] = pt.coordinates as [number, number];
          this.showMarker(
            plng,
            plat,
            res.data.chainage_label,
            this.placeAssetMode ? this.assetIconPath(this.newEventType) : undefined
          );
        }
        if (!this.placeAssetMode) {
          this.statusMessage = `Chainage set: ${res.data.chainage_label}`;
        }
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Locate failed'),
    });
  }

  private applyLocateResult(data: LrsLocateResult): void {
    const editing = this.assetDetailOpen && !!this.selectedAssetId;
    const isLinear = editing
      ? this.assetDetailKind === 'utility'
        ? this.editMeasureToKm > this.editMeasureKm
        : this.editAssetIsLinear
      : this.selectedAssetIsLinear;

    if (this.chainagePickTarget === 'to') {
      if (editing) {
        this.editMeasureToKm = data.measure_km;
      }
      this.measureToKm = data.measure_km;
    } else {
      if (editing) {
        this.editMeasureKm = data.measure_km;
        if (this.assetDetailKind === 'highway' && this.editAssetIsLinear && this.editMeasureToKm <= this.editMeasureKm) {
          const meta = getLrsAssetTypeMeta(this.editEventType);
          this.editMeasureToKm = data.measure_km + (meta?.defaultSpanM || 100) / 1000;
        }
      }
      this.measureKm = data.measure_km;
      if (!editing && this.selectedAssetIsLinear) {
        const meta = getLrsAssetTypeMeta(this.newEventType);
        this.measureToKm = data.measure_km + (meta?.defaultSpanM || 100) / 1000;
      }
      if (this.selectedRoute) {
        this.referenceInput = this.lrs.formatRouteReference(this.selectedRoute.route_code, data.measure_km);
      }
    }

    this.locateResult = data;
    const fromKm = editing ? this.editMeasureKm : this.measureKm;
    const toKm = editing
      ? this.assetDetailKind === 'utility'
        ? this.editMeasureToKm
        : this.editAssetIsLinear
          ? this.editMeasureToKm
          : null
      : this.selectedAssetIsLinear
        ? this.measureToKm
        : null;
    const linear = editing
      ? this.assetDetailKind === 'utility'
        ? this.editMeasureToKm > this.editMeasureKm
        : this.editAssetIsLinear
      : this.selectedAssetIsLinear;
    this.refreshChainageValidation(fromKm, toKm, linear);
    if (this.placeAssetMode && !editing) {
      if (this.selectedAssetIsLinear && this.chainagePickTarget !== 'to') {
        this.chainagePickTarget = 'to';
        this.chainagePickActive = true;
        this.statusMessage = `Start set at ${data.chainage_label || fromKm.toFixed(3) + ' km'}. Click the route to set the end of ${this.selectedAssetLabel}.`;
      } else {
        this.chainagePickTarget = 'from';
        this.chainagePickActive = false;
        const span =
          this.selectedAssetIsLinear && this.measureToKm > this.measureKm
            ? `${formatChainageFromMeters(this.measureKm * 1000)} → ${formatChainageFromMeters(this.measureToKm * 1000)}`
            : data.chainage_label || `${fromKm.toFixed(3)} km`;
        this.statusMessage = `${this.selectedAssetLabel} at ${span}. Click Save to add.`;
      }
    } else {
      this.chainagePickTarget = 'from';
      this.chainagePickActive = false;
    }
    this.loadBoqAtChainage();
  }

  resolvedProjectId(): number | null {
    const raw = this.projectId || this.selectedRoute?.project_id || '';
    const n = parseInt(String(raw), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  loadBoqAtChainage(): void {
    const pid = this.resolvedProjectId();
    if (!this.selectedRouteId || (!pid && !this.selectedRouteId)) {
      this.boqAtChainage = null;
      return;
    }
    const fromKm = this.measureKm;
    const toKm =
      this.selectedAssetIsLinear && this.measureToKm > this.measureKm ? this.measureToKm : this.measureKm;
    this.boqAtChainageLoading = true;
    this.api
      .getBoqItemsAtChainage({
        project_id: pid || undefined,
        route_id: this.selectedRouteId,
        km: fromKm,
        from_km: fromKm,
        to_km: toKm,
      })
      .subscribe({
        next: (res: any) => {
          this.boqAtChainage = res?.data || { item_count: 0, boqs: [], items: [] };
          this.boqAtChainageLoading = false;
        },
        error: () => {
          this.boqAtChainage = { item_count: 0, boqs: [], items: [] };
          this.boqAtChainageLoading = false;
        },
      });
  }

  generateBoqForRange(): void {
    const pid = this.resolvedProjectId();
    if (!pid || !this.selectedRouteId) {
      this.statusMessage = 'Link a numeric project_id on the route to generate BOQ';
      return;
    }
    const fromKm = this.measureKm || 0;
    const toKm =
      this.selectedAssetIsLinear && this.measureToKm > fromKm
        ? this.measureToKm
        : fromKm + Math.max(this.chainageIntervalKm || 1, 0.1);
    const code = `GIS-${this.selectedRoute?.route_code || this.selectedRouteId}-${Date.now().toString().slice(-6)}`;
    this.generatingBoq = true;
    this.api
      .applyGisBoq({
        project_id: pid,
        route_id: this.selectedRouteId,
        from_km: fromKm,
        to_km: toKm,
        domain: 'road',
        target: 'standard',
        boq_code: code,
        boq_name: `GIS BOQ ${this.selectedRoute?.route_code || ''} ${fromKm.toFixed(2)}–${toKm.toFixed(2)} km`,
      })
      .subscribe({
        next: (res: any) => {
          this.generatingBoq = false;
          const boqId = res?.data?.boq_id;
          this.statusMessage = boqId
            ? `BOQ ${code} created (draft RFQ ready)`
            : 'BOQ created from GIS';
          this.loadBoqAtChainage();
          if (boqId) {
            void this.router.navigate(['/home/boq', boqId, 'edit']);
          }
        },
        error: (err) => {
          this.generatingBoq = false;
          this.statusMessage = err?.error?.error || 'Failed to create BOQ from map';
        },
      });
  }

  openBoqFromMap(boqId: number): void {
    void this.router.navigate(['/home/boq', boqId, 'edit']);
  }

  interpolateFromInput(): void {
    if (!this.selectedRouteId) return;
    this.lrs.interpolate(this.selectedRouteId, this.measureKm).subscribe({
      next: (res) => {
        this.locateResult = res.data;
        const pt = res.data.closest_point || res.data.point;
        if (pt?.coordinates) {
          const [lng, lat] = pt.coordinates as [number, number];
          this.showMarker(lng, lat, res.data.chainage_label);
        }
        this.loadBoqAtChainage();
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Interpolate failed'),
    });
  }

  parseReference(): void {
    if (!this.referenceInput.trim()) return;
    this.lrs.parseReference(this.referenceInput).subscribe({
      next: (res) => {
        const data = res.data as { measure_km?: number; route?: LrsRoute };
        if (data?.measure_km != null) this.measureKm = data.measure_km;
        if (data?.route?.id) this.selectRoute(data.route.id);
        else if (this.selectedRouteId) this.interpolateFromInput();
      },
      error: (e) => (this.statusMessage = e?.error?.error || 'Parse failed'),
    });
  }

  addAssetEvent(): void {
    if (!this.selectedRouteId) {
      this.statusMessage = 'Select a route before adding an asset';
      return;
    }
    if (this.assetsLocked) {
      this.statusMessage = 'Assets are locked until Map → Road → LRS all pass';
      return;
    }

    const validation = this.validateChainageRange(
      this.measureKm,
      this.selectedAssetIsLinear ? this.measureToKm : null,
      this.selectedAssetIsLinear
    );
    if (validation) {
      this.chainageValidationError = validation;
      this.statusMessage = validation;
      return;
    }
    this.chainageValidationError = '';

    const label =
      this.newEventLabel.trim() ||
      defaultAssetLabel(this.newEventType, this.measureKm, this.selectedAssetIsLinear ? this.measureToKm : undefined);

    const body: Parameters<LrsService['createEvent']>[1] = {
      measure_km: this.measureKm,
      event_type: this.newEventType,
      label,
      side: this.assetSide || undefined,
      offset_m: this.lateralOffsetM || undefined,
      edge_offset_m: this.edgeOffsetM ?? undefined,
      grade: this.highwayGrade,
      condition_grade: this.conditionGrade || undefined,
      asset_ref: this.assetRef || undefined,
      properties: {
        asset_category: 'highway',
        asset_ref: this.assetRef || undefined,
        condition_grade: this.conditionGrade || undefined,
        event_behavior: this.eventBehavior !== 'default' ? this.eventBehavior : undefined,
        symbol_code: symbolCodeForAssetType(this.newEventType),
        grade: this.highwayGrade,
        ...this.createSpecValues,
      },
    };

    if (this.selectedAssetIsLinear) {
      body.measure_to_km = this.measureToKm;
    }
    if (this.referenceInput.trim()) body.chainage = this.referenceInput;
    if (this.assetValidFrom.trim()) body.valid_from = this.assetValidFrom;
    if (this.assetValidTo.trim()) body.valid_to = this.assetValidTo;

    this.lrs.createEvent(this.selectedRouteId, body).subscribe({
      next: (res) => {
        const saved = res.data;
        const ch =
          saved.chainage_to_label
            ? `${saved.chainage_label || ''} → ${saved.chainage_to_label}`
            : saved.chainage_label || `${this.measureKm.toFixed(3)} km`;
        this.statusMessage = `${this.selectedAssetLabel} saved at ${ch}`;
        this.newEventLabel = '';
        this.assetRef = '';
        this.exitPlaceAssetMode(false);
        this.dashboard.markDirty();
        this.loadRouteMap(this.selectedRouteId!);
        this.activePanel = 'assets';
      },
      error: (e) => {
        if (this.isOfflineError(e)) {
          this.queueOfflineAsset(this.selectedRouteId!, body);
          this.statusMessage = `Offline — asset queued locally (${this.pendingOfflineCount} pending)`;
          this.newEventLabel = '';
          this.assetRef = '';
          this.exitPlaceAssetMode(false);
        } else {
          this.statusMessage = e?.error?.error || 'Save asset failed';
        }
      },
    });
  }

  // --- Offline field collection (TC-LRS-25) ---

  private isOfflineError(e: { status?: number }): boolean {
    return !navigator.onLine || e?.status === 0;
  }

  private readOfflineQueue(): { client_ref: string; route_id: number; body: Record<string, unknown>; queued_at: string }[] {
    try {
      return JSON.parse(localStorage.getItem(this.offlineQueueKey) || '[]');
    } catch {
      return [];
    }
  }

  private writeOfflineQueue(queue: { client_ref: string; route_id: number; body: Record<string, unknown>; queued_at: string }[]): void {
    localStorage.setItem(this.offlineQueueKey, JSON.stringify(queue));
    this.pendingOfflineCount = queue.length;
  }

  private queueOfflineAsset(routeId: number, body: Record<string, unknown>): void {
    const queue = this.readOfflineQueue();
    const clientRef = `off-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    queue.push({
      client_ref: clientRef,
      route_id: routeId,
      body: {
        ...body,
        properties: { ...((body['properties'] as Record<string, unknown>) || {}), client_ref: clientRef, captured_offline: true },
      },
      queued_at: new Date().toISOString(),
    });
    this.writeOfflineQueue(queue);
  }

  syncOfflineQueue(): void {
    const queue = this.readOfflineQueue();
    if (!queue.length || this.offlineSyncing) return;
    if (!navigator.onLine) {
      this.statusMessage = 'Still offline — sync postponed';
      return;
    }
    this.offlineSyncing = true;
    const item = queue[0];
    this.lrs.createEvent(item.route_id, item.body as Parameters<LrsService['createEvent']>[1]).subscribe({
      next: () => {
        this.writeOfflineQueue(queue.slice(1));
        this.offlineSyncing = false;
        if (this.pendingOfflineCount > 0) {
          this.syncOfflineQueue();
        } else {
          this.statusMessage = 'Offline queue synced — no pending items';
          if (this.selectedRouteId === item.route_id) this.loadRouteMap(item.route_id);
        }
      },
      error: (e) => {
        this.offlineSyncing = false;
        if (this.isOfflineError(e)) {
          this.statusMessage = `Sync interrupted — ${this.pendingOfflineCount} still pending`;
        } else {
          // Permanent failure (e.g. validation): drop to avoid blocking the queue.
          this.writeOfflineQueue(queue.slice(1));
          this.statusMessage = `Queued item rejected (${e?.error?.error || 'error'}) — skipped`;
          if (this.pendingOfflineCount > 0) this.syncOfflineQueue();
        }
      },
    });
  }

  clearOfflineQueue(): void {
    this.writeOfflineQueue([]);
    this.statusMessage = 'Offline queue cleared';
  }

  formatLinkedDocumentChainage(doc: LrsDocumentRef): string {
    return lrsChainageLabel(lrsMetaFromDocument(doc)) || `#${doc.id}`;
  }

  panToLinkedDocument(doc: LrsDocumentRef): void {
    this.documentId = doc.id;
    this.lastHydratedDocumentId = doc.id;
    const lrs = lrsMetaFromDocument(doc);
    const km = measureKmFromLrsMeta(lrs);
    if (km != null) this.measureKm = km;
    const chainage = String(lrs['chainage'] || lrs['reference'] || '');
    if (chainage) this.referenceInput = chainage;
    const routeId = Number(lrs['route_id']);
    if (Number.isFinite(routeId) && routeId > 0 && this.selectedRouteId !== routeId) {
      this.pendingDocumentPan = km != null;
      if (this.routes.some((r) => r.id === routeId)) this.selectRoute(routeId);
      else this.pendingRouteId = routeId;
      return;
    }
    if (this.selectedRouteId && km != null) this.interpolateFromInput();
  }

  private syncDocumentFromQuery(qp: ParamMap): void {
    const raw = qp.get('document_id');
    const parsed = raw ? parseInt(raw, 10) : NaN;
    const next = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    if (next === this.lastHydratedDocumentId) return;
    this.documentId = next;
    this.lastHydratedDocumentId = next;
    if (next) this.hydrateLibraryDocument();
  }

  private hydrateLibraryDocument(): void {
    if (!this.documentId) return;
    this.api.getDocumentById(this.documentId).subscribe({
      next: (res: any) => {
        const doc = res?.success ? res.data : res?.data || res;
        const lrs = lrsMetaFromDocument(doc);
        const km = measureKmFromLrsMeta(lrs);
        if (km != null) this.measureKm = km;
        const chainage = String(lrs['chainage'] || lrs['reference'] || '');
        if (chainage) this.referenceInput = chainage;
        const routeId = Number(lrs['route_id']);
        this.statusMessage =
          km != null
            ? `Document #${this.documentId} @ ${km.toFixed(3)} km — Tag to save`
            : `Document #${this.documentId} — pick chainage and Tag`;
        if (Number.isFinite(routeId) && routeId > 0) {
          if (this.selectedRouteId === routeId) {
            if (km != null) this.interpolateFromInput();
          } else if (this.routes.some((r) => r.id === routeId)) {
            this.pendingDocumentPan = km != null;
            this.selectRoute(routeId);
          } else {
            this.pendingRouteId = routeId;
            this.pendingDocumentPan = km != null;
          }
        } else if (this.selectedRouteId && km != null) {
          this.interpolateFromInput();
        }
      },
      error: () => {
        this.statusMessage = `Document #${this.documentId} — pick chainage and Tag`;
      },
    });
  }

  attachDocumentChainage(): void {
    if (!this.documentId || !this.selectedRouteId) return;
    this.lrs
      .attachDocumentLrs(this.documentId, {
        route_id: this.selectedRouteId,
        measure_km: this.measureKm,
        chainage: this.referenceInput || undefined,
      })
      .subscribe({
        next: () => {
          this.statusMessage = `Document #${this.documentId} tagged`;
          this.loadLinkedDocuments(this.selectedRouteId!);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Attach failed'),
      });
  }

  toggleDrawRoute(): void {
    if (!this.map) return;
    if (!this.hasNetwork) {
      this.statusMessage = 'Create a road network first. Routes stay locked until then.';
      return;
    }
    if (this.realignDrawMode) this.toggleRealignRoute();
    if (this.routeEditVerticesMode) this.teardownRouteModify();
    this.drawMode = !this.drawMode;
    this.chainagePickActive = false;
    if (this.osmDrawSnap) {
      this.map.removeInteraction(this.osmDrawSnap);
      this.osmDrawSnap = undefined;
    }
    if (this.drawMode) {
      this.draw = new Draw({ source: this.routeSource, type: 'LineString' });
      const snapSource = this.findOsmNetworkSource() || this.routeSource;
      const tol = this.snapPixelTolerance();
      this.lastDrawSnapTol = tol;
      this.osmDrawSnap = new Snap({ source: snapSource, pixelTolerance: tol });
      this.map.addInteraction(this.osmDrawSnap);
      this.draw.on('drawend', (evt) => {
        const geom = evt.feature.getGeometry() as LineString;
        const coords = geom.getCoordinates().map((c) => toLonLat(c));
        this.saveDrawnRoute({ type: 'LineString', coordinates: coords });
        this.drawMode = false;
        this.draw?.setActive(false);
        this.map?.removeInteraction(this.draw!);
        this.draw = undefined;
        if (this.osmDrawSnap) {
          this.map?.removeInteraction(this.osmDrawSnap);
          this.osmDrawSnap = undefined;
        }
      });
      this.map.addInteraction(this.draw);
    } else if (this.draw) {
      this.map.removeInteraction(this.draw);
      this.draw = undefined;
    }
  }

  private snapPixelTolerance(): number {
    const zoom = this.map?.getView().getZoom() || 14;
    if (zoom >= 18) return 8;
    if (zoom >= 16) return 14;
    if (zoom >= 14) return 22;
    return 32;
  }

  private refreshDrawSnapTolerance(): void {
    if (!this.drawMode || !this.map || !this.osmDrawSnap) return;
    const tol = this.snapPixelTolerance();
    if (tol === this.lastDrawSnapTol) return;
    this.lastDrawSnapTol = tol;
    const snapSource = this.findOsmNetworkSource() || this.routeSource;
    this.map.removeInteraction(this.osmDrawSnap);
    this.osmDrawSnap = new Snap({ source: snapSource, pixelTolerance: tol });
    this.map.addInteraction(this.osmDrawSnap);
  }

  private findOsmNetworkSource(): VectorSource | null {
    if (!this.map) return null;
    let found: VectorSource | null = null;
    this.map.getLayers().forEach((layer) => {
      if (layer.get('name') === 'osm_road_network' && typeof (layer as VectorLayer<VectorSource>).getSource === 'function') {
        found = (layer as VectorLayer<VectorSource>).getSource() || null;
      }
    });
    return found;
  }

  private saveDrawnRoute(geometry: GeoJSON.LineString): void {
    const code = this.newRouteCode.trim() || `ROUTE-${Date.now()}`;
    this.lrs
      .createRoute({
        route_code: code,
        route_name: this.newRouteName.trim() || code,
        geometry,
        project_id: this.projectId || undefined,
        network_id: this.selectedNetworkId || undefined,
        subnetwork_id: this.selectedSubnetworkId || undefined,
        design_speed_kmh: this.newRouteDesignSpeed ?? null,
        terrain_type: this.newRouteTerrain || null,
        row_width_m: this.newRouteRowWidth ?? null,
        carriageway_config: this.newRouteCarriageway || null,
        route_class: this.newRouteClass || null,
      })
      .subscribe({
        next: (res) => {
          this.statusMessage = `Route ${res.data.route_code} created`;
          this.loadRoutes(this.projectId || undefined);
          this.selectRoute(res.data.id);
        },
        error: (e) => (this.statusMessage = e?.error?.error || 'Create route failed'),
      });
  }

  private showMarker(lng: number, lat: number, label?: string, iconSrc?: string): void {
    this.markerSource.clear();
    const f = new Feature({ geometry: new Point(fromLonLat([lng, lat])), label: label || '' });
    if (iconSrc) {
      f.setStyle(
        new Style({
          image: new Icon({
            src: iconSrc,
            width: 28,
            height: 28,
            anchor: [0.5, 0.85],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
          }),
          text: new OlText({
            text: label || '',
            offsetY: -22,
            font: '10px sans-serif',
            fill: new Fill({ color: '#0f172a' }),
            stroke: new Stroke({ color: '#fff', width: 3 }),
          }),
        })
      );
    }
    this.markerSource.addFeature(f);
  }

  private attachToGisMap(): void {
    const connect = (hostMap: Map) => {
      if (this.map) return;
      const overlays = this.createLrsOverlayLayers();
      overlays.forEach((layer) => {
        hostMap.addLayer(layer);
        this.attachedLayers.push(layer);
      });
      this.map = hostMap;
      this.wireMapInteractions(false);
      this.statusMessage = 'LRS layers on GIS map — route + measure model';
      this.geoBridge.refreshMapSize();
    };

    const existing = this.geoBridge.getMap();
    if (existing) {
      connect(existing);
      return;
    }

    this.mapBridgeSub = this.geoBridge.mapReady$.pipe(take(1)).subscribe(connect);
  }

  private createLrsOverlayLayers(): Layer[] {
    this.routeLayer = new VectorLayer({
      source: this.routeSource,
      zIndex: 10,
      style: new Style({ stroke: new Stroke({ color: '#1d4ed8', width: 5 }) }),
    });

    return [
      (() => {
        this.cadastralLayer = new VectorLayer({
          source: this.cadastralSource,
          zIndex: 8,
          visible: this.activePanel === 'cadastral',
          style: (f, res) => this.styleCadastralParcel(f, res ?? 1),
        });
        return this.cadastralLayer;
      })(),
      new VectorLayer({
        source: this.cadastralDrawSource,
        zIndex: 25,
        style: new Style({
          fill: new Fill({ color: 'rgba(37, 99, 235, 0.15)' }),
          stroke: new Stroke({ color: '#2563eb', width: 2, lineDash: [6, 4] }),
        }),
      }),
      this.routeLayer,
      (() => {
        this.carriagewayLayer = new VectorLayer({
          source: this.carriagewaySource,
          zIndex: 10,
          style: new Style({
            stroke: new Stroke({ color: '#94a3b8', width: 3, lineDash: [6, 4] }),
          }),
        });
        return this.carriagewayLayer;
      })(),
      (() => {
        this.lanesLayer = new VectorLayer({
          source: this.lanesSource,
          zIndex: 11,
          visible: false,
          style: (f) => this.styleLane(f),
        });
        return this.lanesLayer;
      })(),
      new VectorLayer({
        source: this.segPreviewSource,
        zIndex: 11,
        style: (f) => {
          const layer = String(f.get('layer') || '');
          if (layer === 'excavation_zone') {
            return new Style({
              stroke: new Stroke({ color: '#f97316', width: 10, lineDash: [14, 10] }),
            });
          }
          return new Style({
            stroke: new Stroke({ color: '#0ea5e9', width: 8, lineDash: [12, 8] }),
          });
        },
      }),
      new VectorLayer({
        source: this.clashSource,
        zIndex: 17,
        style: (f) =>
          new Style({
            image: new CircleStyle({
              radius: 9,
              fill: new Fill({ color: this.clashSeverityColor(String(f.get('severity'))) }),
              stroke: new Stroke({ color: '#fff', width: 2 }),
            }),
            text: new OlText({
              text: String(f.get('code') || ''),
              offsetY: -14,
              font: '9px sans-serif',
              fill: new Fill({ color: '#0f172a' }),
            }),
          }),
      }),
      new VectorLayer({
        source: this.offsetLineSource,
        zIndex: 12,
        style: new Style({
          stroke: new Stroke({ color: '#a855f7', width: 4, lineDash: [10, 6] }),
        }),
      }),
      new VectorLayer({
        source: this.offsetConnectorSource,
        zIndex: 12,
        style: new Style({
          stroke: new Stroke({ color: '#94a3b8', width: 2, lineDash: [4, 4] }),
        }),
      }),
      new VectorLayer({
        source: this.pavementSource,
        zIndex: 11,
        style: new Style({ stroke: new Stroke({ color: '#a855f7', width: 6 }) }),
      }),
      new VectorLayer({
        source: this.maintenanceSource,
        zIndex: 11,
        style: new Style({ stroke: new Stroke({ color: '#f97316', width: 4, lineDash: [8, 6] }) }),
      }),
      (() => {
        this.utilityLineLayer = new VectorLayer({
          source: this.utilityLineSource,
          zIndex: 12,
          declutter: true,
          style: (f, res) => (this.isCatalogVisible(f as Feature) ? styleUtilityFeature(f, res ?? 1) : []),
        });
        return this.utilityLineLayer;
      })(),
      (() => {
        this.utilityClusterLayer = new VectorLayer({
          source: this.utilityPointClusterSource,
          zIndex: 13,
          declutter: true,
          style: (f, res) => styleCluster(f, res ?? 1),
        });
        return this.utilityClusterLayer;
      })(),
      (() => {
        this.linearEventLayer = new VectorLayer({
          source: this.linearEventSource,
          zIndex: 12,
          declutter: true,
          style: (f, res) => (this.isCatalogVisible(f as Feature) ? styleLinearAsset(f, res ?? 1) : []),
        });
        return this.linearEventLayer;
      })(),
      (() => {
        this.pointClusterLayer = new VectorLayer({
          source: this.pointEventClusterSource,
          zIndex: 13,
          declutter: true,
          style: (f, res) => styleCluster(f, res ?? 1),
        });
        return this.pointClusterLayer;
      })(),
      new VectorLayer({
        source: this.calibSource,
        zIndex: 14,
        style: new Style({
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color: '#22c55e' }),
            stroke: new Stroke({ color: '#14532d', width: 2 }),
          }),
        }),
      }),
      (() => {
        this.chainageLayer = new VectorLayer({
          source: this.chainageSource,
          zIndex: 15,
          declutter: true,
          style: (feat, res) =>
            new Style({
              image: new CircleStyle({
                radius: (res ?? 1) > 20 ? 2 : 3,
                fill: new Fill({ color: '#fbbf24' }),
              }),
              text:
                (res ?? 1) <= 6
                  ? new OlText({
                      text: String(feat.get('chainage_label') || ''),
                      offsetY: -10,
                      font: '9px sans-serif',
                    })
                  : undefined,
            }),
        });
        return this.chainageLayer;
      })(),
      (() => {
        this.highlightLayer = new VectorLayer({
          source: this.highlightSource,
          zIndex: 24,
          style: (f) => {
            const g = f.getGeometry();
            if (g instanceof LineString) {
              return new Style({
                stroke: new Stroke({ color: '#f59e0b', width: 10 }),
              });
            }
            return new Style({
              image: new CircleStyle({
                radius: 14,
                fill: new Fill({ color: 'rgba(245, 158, 11, 0.25)' }),
                stroke: new Stroke({ color: '#f59e0b', width: 3 }),
              }),
            });
          },
        });
        return this.highlightLayer;
      })(),
      (() => {
        this.unmLayer = new VectorLayer({
          source: this.unmSource,
          zIndex: 23,
          style: (f) => this.styleUnmFeature(f),
        });
        return this.unmLayer;
      })(),
      new VectorLayer({
        source: this.unmTraceSource,
        zIndex: 22,
        style: (f) => this.styleUnmFeature(f),
      }),
      (() => {
        this.serverClusterLayer = new VectorLayer({
          source: this.serverClusterSource,
          zIndex: 17,
          visible: false,
          style: (f) => this.styleServerCluster(f),
        });
        return this.serverClusterLayer;
      })(),
      new VectorLayer({
        source: this.markerSource,
        zIndex: 18,
        style: new Style({
          image: new CircleStyle({
            radius: 9,
            fill: new Fill({ color: '#16a34a' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
          }),
        }),
      }),
    ];
  }

  private wireMapInteractions(observeHostResize: boolean): void {
    if (!this.map) return;

    this.applyZoomClusterDistance();

    this.clickKey = this.map.on('singleclick', (evt) => {
      this.handleMapBrowserClick(evt);
    });

    this.resolutionKey = this.map.getView().on('change:resolution', () => {
      if (this.clusterDistanceRaf) cancelAnimationFrame(this.clusterDistanceRaf);
      this.clusterDistanceRaf = requestAnimationFrame(() => {
        this.clusterDistanceRaf = 0;
        this.applyZoomClusterDistance();
      });
      this.pointClusterLayer?.changed();
      this.linearEventLayer?.changed();
      this.utilityClusterLayer?.changed();
      this.refreshDrawSnapTolerance();
    });

    this.moveEndKey = this.map.on('moveend', () => {
      if (this.serverClusterMode) this.scheduleServerClusterReload();
    });

    if (observeHostResize && this.mapHost?.nativeElement) {
      const el = this.mapHost.nativeElement;
      const doUpdateSize = () => {
        if (!this.map) return;
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) this.map.updateSize();
      };
      requestAnimationFrame(() => requestAnimationFrame(doUpdateSize));
      this.resizeObserver = new ResizeObserver(() => doUpdateSize());
      this.resizeObserver.observe(el);
    }
  }

  private initMap(): void {
    this.baseLayers = {
      osm: new TileLayer({ source: new OSM(), zIndex: 0 }),
      googleSatellite: new TileLayer({
        source: new XYZ({
          url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
          attributions: '© Google',
        }),
        zIndex: 0,
      }),
      googleHybrid: new TileLayer({
        source: new XYZ({
          url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
          attributions: '© Google',
        }),
        zIndex: 0,
      }),
    };
    this.baseLayer = this.baseLayers.osm;

    const layers = [this.baseLayer, ...this.createLrsOverlayLayers()];

    this.map = new Map({
      target: this.mapHost.nativeElement,
      layers,
      view: new View({ center: fromLonLat([78.9629, 20.5937]), zoom: 5 }),
      controls: defaultControls().extend([new ScaleLine()]),
    });

    this.wireMapInteractions(true);
  }

  private fitToSource(source: VectorSource, maxZoom = 16): void {
    const extent = source.getExtent();
    if (extent && extent[0] !== Infinity && this.map) {
      this.map.getView().fit(extentBuffer(extent, 80), {
        padding: [40, 40, 40, 40],
        maxZoom,
        duration: 400,
      });
    }
  }
}
